import {getSettingsTab,settingsTabFor} from '../features/preferences/settingsTabs';
import {pipelineMenuItems, outreachMenuItems} from '../shared-ui/navigation/pipelineMenu';
import type { WorkspaceNavigationItem } from '../services/WorkspaceNavigationService';
import { hasNativeEmployerSectionMenu } from '../hooks/nativeEmployerSectionMenu';
import { hasNativeWorkspaceSectionMenu, WORKSPACE_NATIVE_SECTIONS } from '../hooks/nativeWorkspaceSectionMenu';
import type { NativeHeaderState } from '../services/NativeBridge';
import { PREFERENCE_SECTIONS, getPreferenceSectionLabel } from '../features/preferences/preferencesSections';
import {
  buildWorkspacesSidebarTabItems,
  buildWorkspaceDetailSidebarTabItems,
  buildEmployerDetailSidebarTabItems,
  buildEmployerFormSidebarTabItems,
  buildAccountingSidebarTabItems,
  buildNativeSidebarNavItems,
  getSidebarNavGroups,
  isPipelineDestination,
  isAccountingDestination,
  accountingAreaItems,
  buildPayrollSidebarTabItems,
  buildProjectSidebarTabItems,
  buildSystemSettingsSidebarTabItems,
  buildTaxSidebarTabItems,
  buildProfileSidebarTabItems,
  buildEmailSidebarTabItems,
} from '../platform/navigation/sidebarNavConfig';
// The Sales hub's section list, shared with the web sidebar so the two cannot
// drift. See SALES_SECTION_SYMBOLS for the native-only half.
import { visibleSalesSections } from '../features/sales/salesSections';
// Same reasoning for Purchases, now that it drills in the same way. See
// PURCHASES_SECTION_SYMBOLS for the native-only half.
import { visiblePurchasesSections } from '../features/purchases/purchasesSections';
import { isProjectReportsTab } from '../features/reports/project/projectReportCatalog';
import {
  isProjectProcurementTab,
  PROJECT_PROCUREMENT_SECTIONS,
} from '../features/projects/procurement/navigation';
import { isOutreachSectionId } from '../features/outreach/constants';
import { signatureNativeButtons, type SignatureHeaderState } from '../features/email/signatureNativeActions';

export type AppView =
  | 'dashboard'
  | 'quantis-ai'
  | 'calendar'
  | 'projects'
  | 'project-detail'
  | 'tasks'
  | 'progress'
  | 'daily-diary'
  | 'deliveries'
  | 'google-tasks'
  | 'interim-valuation'
  | 'interim-valuations'
  | 'user-management'
  | 'workspaces'
  | 'contractor-management'
  | 'supervisor-management'
  | 'system-settings'
  | 'preferences'
  | 'admin'
  | 'client-valuation'
  | 'client-retention'
  | 'client-invoices'
  | 'finance-billing'
  | 'profile'
  | 'organisation'
  | 'team-management'
  | 'supervisor-map'
  | 'employers'
  | 'resource-library'
  | 'assets'
  | 'outreach'
  | 'add-project'
  | 'add-workspace'
  | 'request-client'
  | 'client-detail'
  | 'performance'
  | 'risk-assessment'
  | 'account'
  | 'billing'
  | 'notifications'
  | 'support'
  | 'messages'
  | 'user-form'
  | 'contacts'
  | 'calls'
  | 'pipeline'
  | 'workspace-pipeline'
  | 'email'
  | 'search'
  | 'sales'
  | 'cis'
  | 'purchases'
  | 'payroll'
  | 'accounting'
  | 'tax'
  | 'more';

/** Every view the app can show. Exported because it is also the source of the
 *  app's URLs — AuthenticatedController derives `/${view}` from this — so the
 *  route table validates against the same list that generates them, and the
 *  two cannot drift. */
export const APP_VIEWS: AppView[] = [
  'dashboard',
  'quantis-ai',
  'calendar',
  'projects',
  'project-detail',
  'tasks',
  'progress',
  'daily-diary',
  'deliveries',
  'google-tasks',
  'interim-valuation',
  'interim-valuations',
  'user-management',
  'workspaces',
  'contractor-management',
  'supervisor-management',
  'system-settings',
  'preferences',
  'admin',
  'client-valuation',
  'client-retention',
  'client-invoices',
  'finance-billing',
  'profile',
  'organisation',
  'team-management',
  'supervisor-map',
  'employers',
  'resource-library',
  'assets',
  'outreach',
  'add-project',
  'add-workspace',
  'request-client',
  'client-detail',
  'performance',
  'risk-assessment',
  'account',
  'billing',
  'notifications',
  'support',
  'messages',
  'user-form',
  'contacts',
  'calls',
  'pipeline',
  'workspace-pipeline',
  'email',
  'search',
  'sales',
  'cis',
  'purchases',
  'payroll',
  'accounting',
  'tax',
  'more',
];

/** Sentinel backTarget/view id (not a real AppView) used to make the native
 *  header chevron close an open Takeoff drawing instead of navigating away.
 *  See buildNativeHeaderState's takeoffDrawingOpen branch and the
 *  interception in AuthenticatedController's NativeBridge 'navigate' handler. */
export const TAKEOFF_CLOSE_DRAWING_SENTINEL = '__takeoff_close_drawing__';

/** Return from a full-size Site Photo to its project gallery in native shells. */
export const SITE_PHOTO_CLOSE_FULL_VIEW_SENTINEL = '__site_photo_close_full_view__';

/** Notifications back chevron (sidebar shells): return to the view the bell
 *  was tapped from. Notifications has no sidebar row, so without a chevron
 *  the only exit was hunting through the sidebar. Not a real AppView —
 *  AuthenticatedController intercepts it and routes to the recorded origin
 *  (dashboard when unknown). */
export const NOTIFICATIONS_BACK_SENTINEL = '__notifications_back__';

/** Sentinel backTarget/view id (not a real AppView) used to make the native
 *  header chevron close the full-page "Add Drawings" uploader (back to the
 *  sheet gallery) instead of leaving the project. Same pattern as
 *  TAKEOFF_CLOSE_DRAWING_SENTINEL; the web header already handles this case
 *  via its inTakeoffUploader branch, but the native shell had no equivalent,
 *  so the native chevron fell through to the default project-detail back and
 *  bounced the user all the way out to the Projects list. */
export const TAKEOFF_CLOSE_UPLOADER_SENTINEL = '__takeoff_close_uploader__';

/** Sentinel backTarget/view id used to make the native header chevron step UP
 *  one folder level inside the Add Drawings uploader (rather than close it).
 *  Mirrors DOCUMENTS_FOLDER_UP_SENTINEL — the uploader browses nested Drive
 *  folders, so back walks up the tree first, closing only at the root. */
export const TAKEOFF_UPLOADER_UP_SENTINEL = '__takeoff_uploader_up__';

/** Sentinel backTarget/view id used to make the native header chevron return
 *  to the uploader's source (file-picker) step from the details (review) step,
 *  instead of closing the uploader or stepping up a folder — neither applies
 *  once processing has already run. Mirrors the web header's
 *  inTakeoffUploaderStepBack branch. */
export const TAKEOFF_UPLOADER_BACK_TO_SOURCE_SENTINEL = '__takeoff_uploader_back_to_source__';

/** Sentinel backTarget/view id (not a real AppView) used to make the native
 *  header chevron close the full-page Sheet details editor (back to the sheet
 *  gallery) instead of leaving the project — discarding unsaved edits with no
 *  warning. Same pattern as TAKEOFF_CLOSE_UPLOADER_SENTINEL; sourced from
 *  TakeoffView's `takeoff:state-change` broadcast (`sheetDetailsOpen`). */
export const TAKEOFF_CLOSE_SHEET_DETAILS_SENTINEL = '__takeoff_close_sheet_details__';
/** The export-template preview (Settings → Templates) is open: the macOS
 *  toolbar chevron closes it back to the template library instead of leaving
 *  Settings. Same sentinel pattern as the Takeoff ones above. */
export const TEMPLATE_PREVIEW_CLOSE_SENTINEL = '__template_preview_close__';

/** Sentinel backTarget/view id (not a real AppView) used to make the native
 *  header chevron close an open workspace detail (back to the Directory list)
 *  instead of leaving the Workspaces area entirely. Mirrors
 *  TAKEOFF_CLOSE_DRAWING_SENTINEL's pattern — see buildNativeHeaderState's
 *  macSidebarDrillIn branch and the interception in AuthenticatedController's
 *  NativeBridge 'navigate' handler. */
export const WORKSPACES_CLOSE_DETAIL_SENTINEL = '__workspaces_close_detail__';

/** A documents folder is open inside a project's Documents tab: the native
 *  back chevron must step UP one folder level (like Files does), not leave
 *  the project. Same sentinel pattern as Takeoff/Workspaces above;
 *  DocumentManagementTab listens for 'documents:folder-up'. */
export const DOCUMENTS_FOLDER_UP_SENTINEL = '__documents_folder_up__';

/** A note is open inside a project's Notes tab: the native back chevron
 *  closes it back to the notes table instead of leaving the project. Same
 *  sentinel pattern as the documents folder-up above; ProjectNotesTab listens
 *  for 'project-notes:back-to-list'. */
export const NOTES_CLOSE_DETAIL_SENTINEL = '__notes_close_detail__';

/** A project report is open: the native chevron returns to the report cards. */
export const REPORTS_CLOSE_REPORT_SENTINEL = '__reports_close_report__';

/** A Planner activity editor is open. The native chevron closes the editor
 * through its existing unsaved-changes guard instead of leaving the project. */
export const PLANNER_CLOSE_EDITOR_SENTINEL = '__planner_close_editor__';

export const PROJECT_TAB_PREFIX = 'project-tab:';

/** An interim application is open inside a project's Applications tab: the
 *  native sidebar drills in to the application's own section nav (Summary /
 *  Breakdown / ... / Dashboard), replacing the in-content SECTIONS rail the
 *  web renders in the browser. Section taps ride navigate(toViewId:) with
 *  this prefix; AuthenticatedController re-dispatches them to
 *  InterimApplication as 'interim:navigate-section'. */
export const INTERIM_SECTION_PREFIX = 'interim-section:';

/** Back inside an open interim application closes the application (returns
 *  to the Applications list) instead of leaving the project. Same sentinel
 *  pattern as Takeoff's drawing-close. */
export const INTERIM_CLOSE_APPLICATION_SENTINEL = '__interim_close_application__';

/** Back inside the H&S tab past the hub grid (RAMS list/record/editor) walks
 *  the H&S stack down one level instead of leaving the project. Same sentinel
 *  pattern as interim's close-application; AuthenticatedController re-dispatches
 *  it as 'hs:back', which RAMSList/ProjectHSTab listen for. */
export const HS_BACK_SENTINEL = '__hs_back__';

/** A machine (or a still-unsaved "New machine" draft) is open in the plant
 *  register — either the standalone /plant screen or the register embedded
 *  in a project's H&S tab. The native back chevron closes it back to the
 *  table instead of leaving the screen, and — embedded — takes priority OVER
 *  HS_BACK_SENTINEL: the first tap closes the machine, a second walks the
 *  H&S stack back to the hub. Same sentinel pattern as
 *  NOTES_CLOSE_DETAIL_SENTINEL; AuthenticatedController's native navigate
 *  handler intercepts it and dispatches PLANT_CLEAR_SELECTION_EVENT, which
 *  PlantRegisterView already listens for regardless of which screen mounted
 *  it. */
export const PLANT_CLOSE_DETAIL_SENTINEL = '__plant_close_detail__';

/** A RAMS editor is open inside the project's H&S tab. The macOS sidebar
 *  drills one level deeper than the project tool list and becomes the
 *  document's section outline. Taps are intercepted by
 *  AuthenticatedController and re-dispatched to RAMSEditor. */
export const HS_EDITOR_SECTION_PREFIX = 'hs-editor-section:';

export const HS_EDITOR_SECTION_ITEMS: { id: string; label: string; sfSymbol: string }[] = [
  { id: 'rams-scope', label: 'Scope of works', sfSymbol: 'doc.text' },
  { id: 'rams-lift-plan', label: 'Lifting plan', sfSymbol: 'arrow.up.forward.square' },
  { id: 'rams-standards', label: 'Legal and standards', sfSymbol: 'text.book.closed' },
  { id: 'rams-drawings', label: 'Drawings and documents', sfSymbol: 'doc.on.doc' },
  { id: 'rams-responsibilities', label: 'Key personnel', sfSymbol: 'person.2' },
  { id: 'rams-persons', label: 'Persons at risk', sfSymbol: 'exclamationmark.triangle' },
  { id: 'rams-plant', label: 'Plant and materials', sfSymbol: 'wrench.and.screwdriver' },
  { id: 'rams-coshh', label: 'COSHH', sfSymbol: 'flask' },
  { id: 'rams-permits', label: 'Permits and pre-start', sfSymbol: 'checkmark.seal' },
  { id: 'rams-method', label: 'Method statement', sfSymbol: 'list.number' },
  { id: 'rams-hold-points', label: 'Hold points', sfSymbol: 'hand.raised' },
  { id: 'rams-risks', label: 'Risk assessment', sfSymbol: 'checklist' },
  { id: 'rams-environmental', label: 'Environmental', sfSymbol: 'leaf' },
  { id: 'rams-ppe', label: 'PPE', sfSymbol: 'shield' },
  { id: 'rams-emergency', label: 'Emergency and first aid', sfSymbol: 'cross.case' },
  { id: 'rams-quality-records', label: 'Quality records', sfSymbol: 'tray.full' },
  { id: 'rams-handover', label: 'Handover', sfSymbol: 'flag.checkered' },
  { id: 'rams-references', label: 'References', sfSymbol: 'books.vertical' },
  { id: 'rams-distribution', label: 'Distribution', sfSymbol: 'paperplane' },
  { id: 'rams-briefing', label: 'Briefing and sign-off', sfSymbol: 'signature' },
];

/** Read-only RAMS sections use their own route namespace so opening a record
 *  never enables the editor's Save/Cancel controls. */
export const HS_DOCUMENT_SECTION_PREFIX = 'hs-document-section:';

export const HS_DOCUMENT_SECTION_ITEMS: { id: string; label: string; sfSymbol: string }[] = [
  { id: 'rams-overview', label: 'Overview', sfSymbol: 'info.circle' },
  ...HS_EDITOR_SECTION_ITEMS.filter((item) => item.id === 'rams-lift-plan'),
  ...HS_EDITOR_SECTION_ITEMS.filter((item) => item.id !== 'rams-lift-plan'),
];

/** Mirror of InterimApplication's tabsConfig (id + label) with the SF Symbol
 *  each row renders as in the native sidebars. Keep in sync with
 *  src/features/interim/InterimApplication.tsx tabsConfig. */
export const INTERIM_SECTION_ITEMS: { id: string; label: string; sfSymbol: string }[] = [
  { id: 'summary', label: 'Summary', sfSymbol: 'chart.bar.doc.horizontal' },
  { id: 'breakdown', label: 'Breakdown', sfSymbol: 'list.bullet' },
  { id: 'variations', label: 'Variations', sfSymbol: 'arrow.triangle.2.circlepath' },
  { id: 'materials', label: 'Materials', sfSymbol: 'shippingbox' },
  { id: 'invoice', label: 'Application', sfSymbol: 'doc.text' },
  { id: 'payment', label: 'Payment Outcome', sfSymbol: 'banknote' },
  { id: 'pay-less', label: 'Pay Less Notice', sfSymbol: 'doc.text.magnifyingglass' },
];
/** Editing a user inside a workspace's Users tab drills the sidebar one level
 *  deeper again, the same way an open interim application does: the workspace
 *  section list is replaced by the form's own sections, and EditUserForm drops
 *  its in-content TabsList because this replaces it. */
export const USER_FORM_SECTION_PREFIX = 'user-form-section:';

/** Mirror of EditUserForm's tab list (id + label) with the SF Symbol each row
 *  renders as in the native sidebars. Keep in sync with
 *  src/features/users/EditUserForm.tsx. */
export const USER_FORM_SECTION_ITEMS: { id: string; label: string; sfSymbol: string }[] = [
  { id: 'overview', label: 'Account', sfSymbol: 'person.text.rectangle' },
  { id: 'permissions', label: 'Permissions', sfSymbol: 'lock.shield' },
  { id: 'security', label: 'Security', sfSymbol: 'key' },
  { id: 'info', label: 'Account Info', sfSymbol: 'info.circle' },
];

export const SYSTEM_SETTINGS_TAB_PREFIX = 'system-settings-tab:';
export const PREFERENCES_TAB_PREFIX = 'preferences-tab:';
/** Native tab id that means "no section open": the phone list. */
export const PREFERENCES_LIST_ID = 'list';
export const PROFILE_TAB_PREFIX = 'profile-tab:';
export const WORKSPACES_TAB_PREFIX = 'workspaces-tab:';
/** A workspace detail is open on the macOS sidebar: rows are the workspace's
 *  own section nav (Organisation / Accounts / Quantis / Workspace) rather than
 *  the Directory/Requests/Add Workspace list. See buildNativeNavItems'
 *  workspaces branch and buildWorkspaceDetailSidebarTabItems. */
export const WORKSPACE_DETAIL_TAB_PREFIX = 'workspaces-detail-tab:';
/** An employer is drilled into: sidebar rows are the profile's own sections
 *  (Details / Trading History / ...) rather than the app nav. Mirrors
 *  WORKSPACE_DETAIL_TAB_PREFIX; taps ride navigate(toViewId:) and
 *  AuthenticatedController re-dispatches them as
 *  EMPLOYER_DETAIL_SET_SECTION_EVENT. */
export const EMPLOYER_DETAIL_TAB_PREFIX = 'employers-detail-tab:';
/** The employer add/edit form is open: the sidebar drills one level past the
 *  profile's sections to the form's own (Company / Address / ... / Credit
 *  Check), the same move the edit-user form makes inside a workspace. Taps ride
 *  navigate(toViewId:) and AuthenticatedController re-dispatches them as
 *  EMPLOYER_FORM_SET_SECTION_EVENT. Unlike the profile's sections this page is
 *  never written to the URL, so there is no router leg. */
export const EMPLOYER_FORM_TAB_PREFIX = 'employers-form-tab:';
/** Sentinel backTarget/view id (not a real AppView) that closes an open
 *  employer profile back to the directory. The employers route has no native
 *  back chevron of its own (backTarget 'account' resolves to false on the
 *  sidebar shells) and the web header is suppressed there, so without this the
 *  drilled-in profile is a dead end on macOS/iPad. Same pattern as
 *  WORKSPACES_CLOSE_DETAIL_SENTINEL. */
export const EMPLOYERS_CLOSE_DETAIL_SENTINEL = '__employers_close_detail__';
/** Sentinel backTarget/view id that closes the open rate item form back to the
 *  Resource Library list. The form is an overlay on the library rather than a
 *  route of its own, so the chevron closes it instead of leaving the view. The
 *  web header carries this as its own 'rate-item-back' pill, but the shells
 *  replace that whole row, and resource-library's backTarget 'account' resolves
 *  to no chevron there, so without this the form is a dead end on macOS/iPad,
 *  exactly as the employer profile was. Same pattern as
 *  EMPLOYERS_CLOSE_DETAIL_SENTINEL. */
export const RESOURCE_LIBRARY_ITEM_BACK_SENTINEL = '__resource_library_item_back__';
export const PAYROLL_TAB_PREFIX = 'payroll-tab:';
export const ACCOUNTING_TAB_PREFIX = 'accounting-tab:';
export const SALES_TAB_PREFIX = 'sales-tab:';
export const PURCHASES_TAB_PREFIX = 'purchases-tab:';
export const TAX_TAB_PREFIX = 'tax-tab:';
export const EMAIL_TAB_PREFIX = 'email-tab:';
/** Native sidebar id prefix for a permission-scoped shared mailbox. */
export const EMAIL_SHARED_TAB_PREFIX = 'email-group:';
/** Native header back target while an email thread is open. */
export const EMAIL_THREAD_BACK_SENTINEL = 'email-thread:back';

export const isAppView = (value: string): value is AppView =>
  APP_VIEWS.includes(value as AppView);

export const NATIVE_TAB_TO_VIEW: Record<string, AppView> = {
  home: 'dashboard',
  projects: 'projects',
  messages: 'messages',
  notifications: 'notifications',
  search: 'search',
  // 'more' resolves to the More list, NOT the account screen. It mapped to
  // 'account' from when the native More tab WAS the account screen; the phone
  // shell now has its own More destination, and overflow views publish a back
  // target of 'more', which this mapping was silently rewriting into Account
  // History — so backing out of Contacts landed on a screen the user had
  // never visited.
  more: 'more',
};

export const VIEW_TO_NATIVE_TAB: Partial<Record<AppView, string>> = {
  dashboard: 'home',
  calendar: 'home',
  tasks: 'home',
  'google-tasks': 'home',
  'request-client': 'home',
  'contacts': 'home',
  'pipeline': 'home',
  'workspace-pipeline': 'home',
  'sales': 'home',
  'cis': 'home',
  'purchases': 'home',
  'payroll': 'home',
  'accounting': 'home',
  'tax': 'home',
  projects: 'projects',
  'project-detail': 'projects',
  'add-project': 'projects',
  'interim-valuation': 'projects',
  'interim-valuations': 'projects',
  'client-valuation': 'projects',
  'client-retention': 'projects',
  'client-invoices': 'projects',
  'client-detail': 'projects',
  messages: 'messages',
  notifications: 'notifications',
  support: 'more',
  search: 'search',
  more: 'more',
  profile: 'more',
  organisation: 'more',
  account: 'more',
  billing: 'more',
  performance: 'more',
  'risk-assessment': 'more',
  'supervisor-map': 'more',
  'user-management': 'more',
  'workspaces': 'more',
  'contractor-management': 'more',
  'supervisor-management': 'more',
  'finance-billing': 'more',
  'employers': 'more',
  'resource-library': 'more',
  'assets': 'more',
  'calls': 'more',
  'system-settings': 'more',
  preferences: 'more',
  admin: 'more',
  'team-management': 'more',
  'user-form': 'more',
};

type NativeHeaderConfig = {
  canGoBack: boolean;
  backTarget: string;
  primaryAction: NativePrimaryActionId | null;
  showLargeTitle: boolean;
};

type NativePrimaryActionId =
  | 'add'
  | 'addProject'
  | 'addUser'
  | 'addContact'
  | 'addEnquiry'
  | 'compose'
  | 'uploadDocument'
  | 'clearNotifications'
  | 'saveWorkspace'
  | 'addWorkspace'
  | 'saveEmployer'
  | 'saveProspect'
  | 'savePlant'
  | 'saveProfileChanges'
  | 'sendWorkspaceInvite'
  | 'savePlannerActivity'
  | 'hsSubmit'
  | 'hsIssue';

/** The phone tab bar's own destinations. Everything else is overflow, reached
 *  through More, and needs a chevron back to it. */
const PHONE_TAB_ROOT_VIEWS = new Set<string>([
  'dashboard', 'projects', 'messages', 'notifications', 'more', 'search',
]);

const DEFAULT_HEADER: NativeHeaderConfig = {
  canGoBack: false,
  backTarget: 'dashboard',
  primaryAction: null,
  showLargeTitle: true,
};

const NATIVE_PRIMARY_ACTIONS: Record<
  NativePrimaryActionId,
  { label: string; sfSymbol: string }
> = {
  add: { label: 'Add', sfSymbol: 'plus' },
  addProject: { label: 'Add Project', sfSymbol: 'folder.badge.plus' },
  addUser: { label: 'Add User', sfSymbol: 'person.badge.plus' },
  addContact: { label: 'New Contact', sfSymbol: 'person.badge.plus' },
  addEnquiry: { label: 'New Enquiry', sfSymbol: 'plus.bubble.fill' },
  compose: { label: 'Compose Message', sfSymbol: 'square.and.pencil' },
  uploadDocument: { label: 'Upload Document', sfSymbol: 'square.and.arrow.up' },
  clearNotifications: { label: 'Clear', sfSymbol: 'trash' },
  saveWorkspace: { label: 'Save', sfSymbol: 'checkmark' },
  addWorkspace: { label: 'Add Workspace', sfSymbol: 'plus' },
  saveEmployer: { label: 'Save', sfSymbol: 'checkmark' },
  saveProspect: { label: 'Save', sfSymbol: 'checkmark' },
  // 'Create' while the open machine is still an unsaved draft — see the
  // primaryActionLabel override near resolvedPrimaryAction below, same split
  // PlantDetailState documents for the web Save/Create pill.
  savePlant: { label: 'Save', sfSymbol: 'checkmark' },
  // Handled by useProfileDraft's NativeBridge.on('primaryAction') listener.
  saveProfileChanges: { label: 'Save Changes', sfSymbol: 'checkmark' },
  sendWorkspaceInvite: { label: 'Send Invitation', sfSymbol: 'paperplane.fill' },
  savePlannerActivity: { label: 'Save Activity', sfSymbol: 'checkmark' },
  // Submitting fires 'hs:submit-review', which opens the recipient-picker
  // dialog rather than transitioning directly — same event the web Send for
  // review pill dispatches (headerActions/hs.ts).
  hsSubmit: { label: 'Send for review', sfSymbol: 'paperplane' },
  hsIssue: { label: 'Issue', sfSymbol: 'checkmark.seal' },
};

const NATIVE_HEADER_CONFIG: Partial<Record<AppView, NativeHeaderConfig>> = {
  dashboard: { canGoBack: false, backTarget: 'dashboard', primaryAction: null, showLargeTitle: true },
  projects: { canGoBack: false, backTarget: 'dashboard', primaryAction: 'addProject', showLargeTitle: true },
  'project-detail': { canGoBack: true, backTarget: 'projects', primaryAction: 'add', showLargeTitle: false },
  'add-project': { canGoBack: true, backTarget: 'projects', primaryAction: null, showLargeTitle: false },
  'add-workspace': { canGoBack: true, backTarget: 'workspaces', primaryAction: null, showLargeTitle: false },
  tasks: { canGoBack: false, backTarget: 'dashboard', primaryAction: 'add', showLargeTitle: true },
  progress: { canGoBack: true, backTarget: 'dashboard', primaryAction: null, showLargeTitle: false },
  'daily-diary': { canGoBack: true, backTarget: 'dashboard', primaryAction: null, showLargeTitle: false },
  deliveries: { canGoBack: true, backTarget: 'dashboard', primaryAction: null, showLargeTitle: false },
  'google-tasks': { canGoBack: true, backTarget: 'tasks', primaryAction: null, showLargeTitle: false },
  messages: { canGoBack: false, backTarget: 'dashboard', primaryAction: 'compose', showLargeTitle: true },
  notifications: { canGoBack: false, backTarget: 'dashboard', primaryAction: 'clearNotifications', showLargeTitle: true },
  support: { canGoBack: false, backTarget: 'dashboard', primaryAction: null, showLargeTitle: true },
  more: { canGoBack: false, backTarget: 'dashboard', primaryAction: null, showLargeTitle: true },
  search: { canGoBack: false, backTarget: 'dashboard', primaryAction: null, showLargeTitle: false },
  profile: { canGoBack: false, backTarget: 'dashboard', primaryAction: null, showLargeTitle: true },
  organisation: { canGoBack: false, backTarget: 'dashboard', primaryAction: null, showLargeTitle: true },
  account: { canGoBack: false, backTarget: 'dashboard', primaryAction: null, showLargeTitle: true },
  billing: { canGoBack: true, backTarget: 'account', primaryAction: null, showLargeTitle: false },
  performance: { canGoBack: true, backTarget: 'account', primaryAction: null, showLargeTitle: false },
  'risk-assessment': { canGoBack: true, backTarget: 'account', primaryAction: null, showLargeTitle: false },
  'supervisor-map': { canGoBack: true, backTarget: 'account', primaryAction: null, showLargeTitle: false },
  'interim-valuations': { canGoBack: true, backTarget: 'projects', primaryAction: null, showLargeTitle: false },
  'interim-valuation': { canGoBack: true, backTarget: 'interim-valuations', primaryAction: null, showLargeTitle: false },
  'request-client': { canGoBack: true, backTarget: 'dashboard', primaryAction: null, showLargeTitle: false },
  'client-detail': { canGoBack: true, backTarget: 'workspaces', primaryAction: null, showLargeTitle: false },
  'client-valuation': { canGoBack: true, backTarget: 'client-detail', primaryAction: null, showLargeTitle: false },
  'client-retention': { canGoBack: true, backTarget: 'client-detail', primaryAction: null, showLargeTitle: false },
  'client-invoices': { canGoBack: true, backTarget: 'client-detail', primaryAction: null, showLargeTitle: false },
  'user-management': { canGoBack: true, backTarget: 'account', primaryAction: 'addUser', showLargeTitle: true },
  // primaryAction is Add Workspace, not addUser - the directory creates
  // workspaces; user management lives inside each workspace's Users tab.
  'workspaces': { canGoBack: true, backTarget: 'account', primaryAction: 'addWorkspace', showLargeTitle: true },
  'contractor-management': { canGoBack: true, backTarget: 'account', primaryAction: 'add', showLargeTitle: true },
  'supervisor-management': { canGoBack: true, backTarget: 'account', primaryAction: 'add', showLargeTitle: true },
  'finance-billing': { canGoBack: true, backTarget: 'account', primaryAction: null, showLargeTitle: true },
  'employers': { canGoBack: true, backTarget: 'account', primaryAction: 'add', showLargeTitle: true },
  'resource-library': { canGoBack: true, backTarget: 'account', primaryAction: null, showLargeTitle: true },
  // backTarget is 'dashboard', not 'account': the register moved off the
  // admin nav into each project's H&S hub, so Account is no longer how a
  // native user reaches /plant — it's a deep-link/reachability address now.
  'assets': { canGoBack: true, backTarget: 'dashboard', primaryAction: 'add', showLargeTitle: true },
  'system-settings': { canGoBack: true, backTarget: 'account', primaryAction: null, showLargeTitle: true },
  preferences: { canGoBack: true, backTarget: 'account', primaryAction: null, showLargeTitle: true },
  'contacts': { canGoBack: false, backTarget: 'dashboard', primaryAction: 'addContact', showLargeTitle: true },
  'calls': { canGoBack: false, backTarget: 'dashboard', primaryAction: null, showLargeTitle: true },
  'pipeline': { canGoBack: false, backTarget: 'dashboard', primaryAction: 'addEnquiry', showLargeTitle: true },
  'workspace-pipeline': { canGoBack: false, backTarget: 'dashboard', primaryAction: 'addEnquiry', showLargeTitle: true },
  'email': { canGoBack: false, backTarget: 'dashboard', primaryAction: 'compose', showLargeTitle: true },
  admin: { canGoBack: true, backTarget: 'account', primaryAction: null, showLargeTitle: true },
  sales: { canGoBack: false, backTarget: 'dashboard', primaryAction: null, showLargeTitle: true },
  cis: { canGoBack: false, backTarget: 'dashboard', primaryAction: 'add', showLargeTitle: true },
  purchases: { canGoBack: false, backTarget: 'dashboard', primaryAction: null, showLargeTitle: true },
  payroll: { canGoBack: false, backTarget: 'dashboard', primaryAction: null, showLargeTitle: true },
  accounting: { canGoBack: false, backTarget: 'dashboard', primaryAction: null, showLargeTitle: true },
  tax: { canGoBack: false, backTarget: 'dashboard', primaryAction: null, showLargeTitle: true },
  'team-management': { canGoBack: true, backTarget: 'account', primaryAction: 'addUser', showLargeTitle: true },
  'user-form': { canGoBack: true, backTarget: 'user-management', primaryAction: null, showLargeTitle: false },
};

const SF: Record<string, string> = {
  dashboard: 'house.fill',
  projects: 'folder.fill',
  tasks: 'checkmark.square.fill',
  messages: 'message.fill',
  notifications: 'bell.fill',
  'request-client': 'person.badge.plus',
  'user-management': 'person.2.fill',
  'workspaces': 'building.2.fill',
  'finance-billing': 'creditcard.fill',
  'employers': 'building.fill',
  'supervisor-management': 'person.2.badge.gearshape.fill',
  'resource-library': 'books.vertical.fill',
  'assets': 'truck.box.fill',
  'system-settings': 'gearshape.fill',
  preferences: 'slider.horizontal.3',
  'calls': 'phone.fill',
  'pipeline': 'chart.bar.doc.horizontal.fill',
  'workspace-pipeline': 'chart.bar.doc.horizontal.fill',
  profile: 'person.circle.fill',
  organisation: 'building.columns.fill',
  billing: 'creditcard.fill',
  performance: 'chart.line.uptrend.xyaxis',
  'risk-assessment': 'shield.fill',
  account: 'clock.arrow.circlepath',
};

/** Translate the shared web-sidebar definitions into the grouped rows expected
 * by the native sidebar. Heading records become native section names instead
 * of rows, keeping both surfaces on one source of truth. */
function buildNativeFinanceHubItems(
  items: ReturnType<typeof buildPayrollSidebarTabItems>,
  prefix: string,
) {
  const nativeItems: Array<{ id: string; label: string; sfSymbol: string; section: string }> = [];
  let section = 'main';

  for (const item of items) {
    if (item.kind === 'heading') {
      section = item.label;
      continue;
    }
    nativeItems.push({
      id: `${prefix}${item.id}`,
      label: item.label,
      sfSymbol: item.sfSymbol ?? 'circle',
      section,
    });
  }

  return nativeItems;
}

/** Sales hub sub-items. Order mirrors SECTIONS in SalesView.tsx. */
/** SF Symbol per Sales section, keyed by SalesSectionId.
 *
 *  Only the GLYPH lives here. Which sections exist, their labels, their order
 *  and who may see them come from features/sales/salesSections.ts, the same
 *  list the web sidebar renders — the symbol is the one genuinely
 *  native-specific part, since the web carries a React component.
 *
 *  This was a second, hand-kept copy of the whole list, and it had drifted
 *  exactly as such copies do: it was missing Subscriptions entirely, it applied
 *  no role filter, and it disagreed with the web on four labels. */
const SALES_SECTION_SYMBOLS: Record<string, string> = {
  overview:          'square.grid.2x2.fill',
  invoices:          'doc.text.fill',
  subscriptions:     'arrow.triangle.2.circlepath',
  'payment-links':   'link',
  'online-payments': 'creditcard.fill',
  quotes:            'doc.plaintext.fill',
  products:          'archivebox.fill',
  customers:         'person.2.fill',
  settings:          'gearshape.fill',
};

/** SF Symbol per Purchases section, keyed by PurchasesSectionId. Same split as
 *  SALES_SECTION_SYMBOLS above, for the same reason: which sections exist,
 *  their labels and order come from features/purchases/purchasesSections.ts,
 *  the same list the web sidebar renders, so only the native-specific glyph
 *  lives here. */
const PURCHASES_SECTION_SYMBOLS: Record<string, string> = {
  overview:               'square.grid.2x2.fill',
  bills:                  'doc.text.fill',
  'online-bill-payments': 'creditcard.fill',
  'purchase-orders':      'list.clipboard.fill',
  expenses:               'receipt.fill',
  merchants:              'storefront.fill',
  settings:               'gearshape.fill',
};

const PROFILE_SECTION_TITLES: Record<string, string> = {
  overview: 'Settings',
  details: 'Settings',
  availability: 'Availability',
  calls: 'Availability',
  security: 'Sign-in and security',
  'account-email': 'Sign-in and security',
  phone: 'Calls and lines',
  connections: 'Connected accounts',
  'connections/google-workspace': 'Google Workspace',
  'connections/microsoft-365': 'Microsoft 365',
  'connections/gmail-addon': 'Gmail add-on',
  'connections/push-notifications': 'Push Notifications',
  signature: 'Email signatures',
  'workspace/overview': 'Organisation',
  'workspace/integrations': 'Integrations',
  'workspace/integrations/xero': 'Integrations',
  'workspace/integrations/google-workspace': 'Integrations',
  'workspace/integrations/gmail-addon': 'Gmail add-on',
  'workspace/integrations/push-notifications': 'Integrations',
  'workspace/trade-profile': 'Organisation',
  'workspace/stripe-invoices': 'Billing',
  'workspace/credit': 'Credit report',
  'workspace/users': 'Users',
  'workspace/documents': 'Documents',
  'workspace/bots': 'AI Agents',
  'workspace/users/invite': 'Invite a member',
};

const SETTINGS_SECTION_TITLES: Record<string, string> = {
  integrations: 'Platform integrations',
  'integrations/xero': 'Xero',
  'integrations/stripe': 'Stripe',
  'integrations/google-workspace': 'Google Workspace',
  'integrations/microsoft-365': 'Microsoft 365',
  'integrations/gmail-addon': 'Gmail add-on',
  'integrations/smtp': 'Email (SMTP)',
  'integrations/supabase': 'Supabase',
  'integrations/cloud-build': 'Cloud Build',
  'integrations/google-maps': 'Google Maps',
  'integrations/companies-house': 'Companies House',
  'integrations/creditsafe': 'CreditSafe',
  'integrations/ons': 'ONS',
  'integrations/push-notifications': 'Push Notifications',
  'integrations/apple-wallet': 'Apple Wallet',
  workflows: 'Workflows',
  automation: 'Workflows',
  templates: 'Export layouts',
  'templates/email': 'Messages',
  'templates/notifications': 'Messages',
  'templates/documents': 'Document templates',
  'templates/outreach': 'Outreach Templates',
  'templates/material-enquiry': 'Material Enquiry',
  'templates/interim-application': 'Interim Application',
  'templates/toolbox-talk-sign-in': 'Toolbox Talk Sign-in Sheet',
  'templates/takeoff-sheet': 'Takeoff Measurement Sheet',
  'templates/bbs-calculator': 'Bar Bending Schedule Calculator',
  data: 'Data',
  audit: 'Audit Trail',
  rfis: 'RFI Register',
  // These six fell through to the generic "Settings" in the window toolbar and
  // native title bar while the sidebar named them properly.
  agents: 'AI Agents',
  'integrations/agents': 'AI Agents',
  'integrations/shared-inboxes': 'Shared Inboxes',
  'workflows/trigger-catalog': 'Workflows',
  'boq-formatting': 'BOQ styles',
  'support-tickets': 'Support Tickets',
};

export function getAppViewTitle({
  currentView,
  selectedProjectName,
  selectedProjectNumber,
  selectedClientName,
  systemSettingsActiveTab,
  preferencesSection,
  profileActiveTab,
  emailActiveTab,
}: {
  currentView: AppView;
  selectedProjectName: string | null;
  /** Project reference number (e.g. "1143"). When truthy, appended to the
   *  project title as " - 1143" so every tab shows the number in the header
   *  and native toolbar without patching each tab individually. Plain hyphen
   *  and no "#": document.title already separates with " | Quantis", so a
   *  second pipe inside the title would read as another level. */
  selectedProjectNumber?: string | null;
  selectedClientName: string | null;
  systemSettingsActiveTab?: string;
  preferencesSection?: string | null;
  profileActiveTab?: string;
  emailActiveTab?: string;
}) {
  switch (currentView) {
    case 'dashboard':
      return 'Home';
    case 'quantis-ai':
      return 'QuantisAI';
    case 'calendar':
      return 'Calendar';
    case 'projects':
      return 'Projects';
    case 'project-detail': {
      const name = selectedProjectName || 'Project';
      return selectedProjectNumber ? `${name} - ${selectedProjectNumber}` : name;
    }
    case 'tasks':
      return 'Tasks';
    case 'progress':
      return 'Submit Progress';
    case 'daily-diary':
      return 'Daily Diary';
    case 'deliveries':
      return 'Deliveries';
    case 'google-tasks':
      return 'Google Tasks';
    case 'messages':
      return 'Messages';
    case 'notifications':
      return 'Notifications';
    case 'support':
      return 'Support';
    case 'interim-valuation':
      return 'Interim Application';
    case 'system-settings':
      return (systemSettingsActiveTab && SETTINGS_SECTION_TITLES[systemSettingsActiveTab])
        || 'Settings';
    case 'preferences':
      return getPreferenceSectionLabel(preferencesSection) ?? 'Preferences';
    case 'user-management':
      return 'User Management';
    case 'workspaces':
      return 'Workspaces';
    case 'contractor-management':
      return 'Contractor Management';
    case 'client-detail':
      return selectedClientName || 'Workspace';
    case 'client-valuation':
      return 'Client Valuation';
    case 'client-retention':
      return 'Client Retention';
    case 'client-invoices':
      return 'Client Invoices';
    case 'finance-billing':
      return 'Billing';
    case 'employers':
      return 'Employers';
    case 'supervisor-management':
      return 'Supervisor Management';
    case 'team-management':
      return 'Team Management';
    case 'resource-library':
      return 'Resource Library';
    case 'assets':
      return 'Assets';
    case 'outreach':
      return 'Outreach';
    case 'interim-valuations':
      return 'Interim Valuations';
    case 'add-project':
      return 'New Project';
    case 'add-workspace':
      return 'Add Workspace';
    case 'request-client':
      return 'Request Business';
    case 'profile':
      return profileActiveTab?.startsWith('workspace/bots/')
        ? 'AI Agents'
        : profileActiveTab?.startsWith('workspace/users/')
        ? PROFILE_SECTION_TITLES['workspace/users']
        : profileActiveTab?.startsWith('workspace/documents/')
        ? PROFILE_SECTION_TITLES['workspace/documents']
        : (profileActiveTab && PROFILE_SECTION_TITLES[profileActiveTab]) || 'Settings';
    case 'organisation':
      return 'Workspace Settings';
    case 'account':
      return profileActiveTab?.startsWith('preferences/') ? getSettingsTab(settingsTabFor(profileActiveTab.slice('preferences/'.length)) ?? 'general').label : (profileActiveTab && PROFILE_SECTION_TITLES[profileActiveTab]) || 'Settings';
    case 'billing':
      return 'Membership';
    case 'performance':
      return 'Performance';
    case 'supervisor-map':
      return 'Supervisor Map';
    case 'risk-assessment':
      return 'Risk Assessment';
    case 'admin':
      return 'Administration';
    case 'user-form':
      return 'Add User';
    case 'contacts':
      return 'Contacts';
    case 'calls':
      return 'Calls';
    case 'pipeline':
      return 'Projects';
    case 'workspace-pipeline':
      return 'Projects';
    case 'email':
      return 'Email';
    case 'search':
      return 'Search';
    case 'sales':
      return 'Sales';
    case 'cis':
      return 'CIS Returns';
    case 'purchases':
      return 'Purchases';
    case 'payroll':
      return 'Payroll';
    case 'accounting':
      return 'Accounting';
    case 'tax':
      return 'Tax';
    case 'more':
      return 'More';
    default:
      return 'Quantis';
  }
}

import { hasNativeSectionMenu } from '../hooks/nativeSectionMenu';
import { isProjectWriteAction } from '../platform/navigation/headerActions/projectReadOnly';

export function buildNativeHeaderState({
  currentView,
  title,
  projectActiveTab,
  activeProjectId = null,
  activeProjectName = null,
  workspacesActiveTab,
  preferencesSection,
  profileActiveTab,
  profileDetailsDirty = false,
  profileSignatureState = null,
  workspaceDetail,
  workspaceUsersListVisible = false,
  workspaceInviteState = null,
  workspaceSaveState,
  employersListVisible = false,
  employerDetailName = null,
  employerDetailState,
  employerFormState,
  prospectDetailState = null,
  hasNativeSidebar = false,
  hasNativeTabBar = false,
  canAddProject = true,
  canManageApplications = false,
  canEditPlanner = false,
  projectReadOnly = false,
  plannerEditorState = null,
  navButtons,
  leadingNavButtons,
  subtitle,
  viewSwitcher,
  secondaryAction,
  secondaryActionLabel,
  secondaryActionSymbol,
  secondaryMenuItems,
  takeoffDrawingOpen = false,
  sitePhotoFullViewOpen = false,
  takeoffUploaderOpen = false,
  takeoffUploaderDepth = 0,
  takeoffUploaderStep = null,
  takeoffSheetDetailsOpen = false,
  documentsFolderOpen = false,
  workspaceDocumentsFolderOpen = false,
  notesDetailOpen = false,
  reportOpen = false,
  interimApplicationOpen = false,
  templatePreviewOpen = false,
  resourceLibraryItemFormOpen = false,
  emailThreadOpen = false,
  hsView = null,
  hsStatus = null,
  hsCanIssue = false,
  hsSection = null,
  plantDetailOpen = false,
  plantDetailState = null,
}: {
  currentView: AppView;
  title: string;
  /** Optional second line for compact native list headers. */
  subtitle?: NativeHeaderState['subtitle'];
  projectActiveTab?: string | null;
  /** Current project context used by native project-owned creation sheets. */
  activeProjectId?: string | null;
  activeProjectName?: string | null;
  /** Active tab inside the Workspaces admin area - drives the macOS drill-in
   *  chevron's target: sub-tabs go back to the clients list first, and only
   *  the list itself exits to Home. */
  workspacesActiveTab?: 'clients' | 'requests' | 'add-client' | null;
  /** Open Profile panel, so the shell's section menu can mark its current row. */
  /** Open Preferences section, or null for the phone list. */
  preferencesSection?: string | null;
  profileActiveTab?: string | null;
  /** The Profile > Personal details staged draft is dirty. Feeds the native
   *  Save/Cancel toolbar pair — published HERE, not from useProfileDraft,
   *  because the shell resets absent header fields (no merge) and a partial
   *  publish from the hook stripped the section back chevron. */
  profileDetailsDirty?: boolean;
  /** Mounted shared signature editor, including a self-profile inside Users. */
  profileSignatureState?: SignatureHeaderState | null;
  /** Open workspace on lg+. When set, the macOS toolbar title becomes the
   *  workspace name and the back chevron closes the detail (back to the
   *  Directory list) instead of retargeting at Home. */
  workspaceDetail?: { clientId: string; clientName: string; activeTab: string } | null;
  /** True while the Users sub-tab shows its list rather than an add / edit user
   *  form. Gates the Add User toolbar action so it doesn't sit over a form the
   *  user is already filling in. */
  workspaceUsersListVisible?: boolean;
  workspaceInviteState?: {
    visible: boolean;
    actionsInHeader: boolean;
    canSubmit: boolean;
    submitting: boolean;
  } | null;
  /** WorkspaceOverview's 'workspace:state-change' broadcast (dirty/saving).
   *  Drives the Save primary action + Cancel secondary action below on BOTH
   *  native shells (macOS sidebar and iOS tab bar), for both surfaces that
   *  mount the form: the admin Workspaces detail and the self-serve
   *  Organisation settings. The web header pills (which normally surface
   *  Save/Cancel) are suppressed in the native shells, so without this the
   *  form is un-saveable there. */
  workspaceSaveState?: { hasChanges: boolean; isSaving: boolean } | null;
  /** True while the Employers screen shows its directory rather than a
   *  drilled-in profile or the add form. Gates the Add toolbar action the same
   *  way workspaceUsersListVisible does, and the native search bar (see
   *  isNativeContextualSearchRoute). */
  employersListVisible?: boolean;
  /** Name of the drilled-in employer, if any. Becomes the toolbar title and
   *  turns the chevron into a close-the-profile control — the same treatment
   *  an open workspace detail gets. */
  employerDetailName?: string | null;
  /** EmployerProfilePanel's 'employer-detail:state-change' broadcast. The
   *  profile is always editable, so `hasChanges` alone decides whether Save and
   *  Cancel are published — and they only exist on the shells via this route. */
  employerDetailState?: { isSubmitting: boolean; hasChanges: boolean } | null;
  /** EmployerForm's 'employer-form:state-change' broadcast. Both shells
   *  suppress the web header, so its Save/Cancel pills only exist natively if
   *  they are published from here. */
  employerFormState?: { isEdit: boolean; isSubmitting: boolean; hasChanges: boolean } | null;
  /** Outreach ProspectDetail's 'prospect-detail:state-change' broadcast. Same
   *  always-live contract as employerDetailState: Save and Cancel exist only
   *  once something changed, and on the shells only through here, because the
   *  web header that draws them is not rendered there. */
  prospectDetailState?: { hasChanges: boolean; saving: boolean } | null;
  hasNativeSidebar?: boolean;
  hasNativeTabBar?: boolean;
  /** Creating projects is internal-team only (super_admin / internal). When
   *  false, the Projects header's New Project (folder+) action is dropped. */
  canAddProject?: boolean;
  /** Only super_admin / internal may create interim applications, so the
   *  Applications tab's header "+" is hidden for read-only viewers. */
  canManageApplications?: boolean;
  /** Mirrors the Planner editor's role gate. The native iPhone header must
   * not show an actionable `+` to a read-only/service account. */
  canEditPlanner?: boolean;
  /** A former client's project viewed by staff: no write action is offered. */
  projectReadOnly?: boolean;
  /** Planner editor state published by ProjectPlannerTab. While visible the
   * native shell owns the title, close chevron, save action and iPhone tab-bar
   * visibility, so the web form does not duplicate app chrome. */
  plannerEditorState?: {
    visible: boolean;
    editing: boolean;
    canEdit: boolean;
    canSubmit: boolean;
    saving: boolean;
    /** iPhone keeps the project toolbar in context while the editor is
     * presented as a bottom drawer. Other native shells can let the toolbar
     * own the editor title and actions. */
    presentation?: 'dialog' | 'drawer' | 'native-header';
  } | null;
  /** Extra toolbar icon buttons injected by the caller (e.g. macOS global
   *  Messages + Notifications buttons). Merged into the returned state so a
   *  single sendHeaderState call carries both title context and global actions. */
  navButtons?: NativeHeaderState['navButtons'];
  /** Leading navigation buttons for native shells. Kept separate from the
   * trailing action cluster so mailbox/section navigation remains predictable. */
  leadingNavButtons?: NativeHeaderState['leadingNavButtons'];
  /** BOQ bill/summary picker for the macOS native toolbar. Only included when
   *  the active tab is 'pricing'; omitted for all other views/tabs so the
   *  native shell clears any stale switcher. */
  viewSwitcher?: NativeHeaderState['viewSwitcher'];
  /** Anchored toolbar menu (macOS SwiftUI Menu). Only forwarded when provided so
   *  views that own their own secondary action (ChatWindow) aren't clobbered. */
  secondaryAction?: NativeHeaderState['secondaryAction'];
  secondaryActionLabel?: NativeHeaderState['secondaryActionLabel'];
  secondaryActionSymbol?: NativeHeaderState['secondaryActionSymbol'];
  secondaryMenuItems?: NativeHeaderState['secondaryMenuItems'];
  /** True while a Takeoff sheet is open in the drawing canvas (as opposed to
   *  the sheet gallery). The native toolbar back chevron must then close the
   *  drawing (back to the gallery) instead of leaving the project — see
   *  the canGoBack/backTarget override below. Sourced from the
   *  `takeoff:canvas-state-change` broadcast, which EVERY takeoff viewer must
   *  publish while a sheet is open and clear on unmount (TakeoffPreviewCanvas,
   *  Takeoff3DViewport, ApsPdfViewport). A viewer that skips it leaves the
   *  native chevron with nothing to close, stranding the estimator in the
   *  drawing. */
  takeoffDrawingOpen?: boolean;
  /** A full-size Site Photo replaces the gallery in the native header. */
  sitePhotoFullViewOpen?: boolean;
  /** True while the full-page "Add Drawings" uploader is open on the Takeoff
   *  tab. Highest takeoff back priority (it sits above both the gallery and any
   *  open drawing): the native back chevron closes the uploader
   *  (TAKEOFF_CLOSE_UPLOADER_SENTINEL) instead of leaving the project. Sourced
   *  from TakeoffView's `takeoff:state-change` broadcast (`uploaderOpen`). */
  takeoffUploaderOpen?: boolean;
  /** How many folder levels deep the uploader is (0 = project root). >0 makes
   *  the native back chevron step up a folder (TAKEOFF_UPLOADER_UP_SENTINEL)
   *  instead of closing the uploader. */
  takeoffUploaderDepth?: number;
  /** The uploader's current step ('source' | 'processing' | 'details').
   *  On the review step ('details') the native back chevron
   *  steps BACK through the flow (TAKEOFF_UPLOADER_BACK_TO_SOURCE_SENTINEL)
   *  rather than closing the uploader or stepping up a folder — folder depth is
   *  meaningless once the files are parsed, and closing outright would bin the
   *  batch mid-review. It lands on the source step, the only step before it.
   *  Sourced from TakeoffUploader's `takeoff:upload-nav` broadcast. */
  takeoffUploaderStep?: string | null;
  /** True while the full-page Sheet details editor is open on the Takeoff tab.
   *  Same priority as the uploader (both are full-page overlays above the
   *  gallery, mutually exclusive with it and each other): the native back
   *  chevron closes the editor (TAKEOFF_CLOSE_SHEET_DETAILS_SENTINEL) instead
   *  of leaving the project. Sourced from TakeoffView's `takeoff:state-change`
   *  broadcast (`sheetDetailsOpen`). */
  takeoffSheetDetailsOpen?: boolean;
  /** True while a documents folder is open inside the project Documents tab:
   *  the native back chevron steps up one folder (DOCUMENTS_FOLDER_UP_SENTINEL)
   *  instead of leaving the project. Sourced from DocumentManagementTab's
   *  'documents:folder-state-change' broadcast; the caller gates it on the
   *  documents tab being active. */
  documentsFolderOpen?: boolean;
  /** Same for the workspace file browser (a company's Documents page or Profile > Workspace > Documents), which is
   *  not a project tab: a folder is open, so the chevron steps up instead of closing the company. */
  workspaceDocumentsFolderOpen?: boolean;
  notesDetailOpen?: boolean;
  reportOpen?: boolean;
  /** An interim application is open inside the Applications tab: the native
   *  back chevron closes the application (INTERIM_CLOSE_APPLICATION_SENTINEL)
   *  instead of leaving the project. */
  interimApplicationOpen?: boolean;
  /** The takeoff export-template preview is open inside Settings → Templates:
   *  the native chevron closes it back to the template library rather than
   *  leaving Settings, so the screen needs no back control of its own. */
  templatePreviewOpen?: boolean;
  /** A rate item form is open over the Resource Library list: the native
   *  chevron closes the form (RESOURCE_LIBRARY_ITEM_BACK_SENTINEL) rather than
   *  leaving the library. Sourced from RateItemPage's
   *  'resource-library-item:state-change' broadcast. The list itself keeps no
   *  chevron; the native sidebar is its way out, as on every other view. */
  resourceLibraryItemFormOpen?: boolean;
  /** An email thread is open in the reading pane; native shells use the
   *  global back chevron and thread actions instead of an in-content toolbar. */
  emailThreadOpen?: boolean;
  /** ProjectHSTab's 'hs:state-change' broadcast (null when off the H&S tab).
   *  Past the hub grid the native chevron walks the H&S stack down one level
   *  (HS_BACK_SENTINEL) instead of leaving the project, and the + primary
   *  action publishes only on the RAMS list. Both shells suppress the web
   *  header, so without this the H&S drill has no way back and no create
   *  affordance there. */
  hsView?: 'hub' | 'list' | 'record' | 'editor' | null;
  /** The open RAMS record's status ('draft' | 'submitted' | 'approved' |
   *  'issued' | 'superseded' | 'archived'), record view only. Drives the
   *  native primary action (Send for review / Issue) and the anchored
   *  lifecycle menu — the web header pills that carry these are suppressed
   *  on both shells, so this is the only way to act on a RAMS record
   *  natively. Approving a submitted record is the EMPLOYER's action on the
   *  public review page, never a native primary action here. */
  hsStatus?: string | null;
  /** Record view, draft only: the issue-completeness gate. Mirrors the web
   *  Issue pill's disabled state by suppressing the native primary action
   *  entirely (native has no disabled state, absence is the signal). */
  hsCanIssue?: boolean;
  /** Which H&S section is open, mirroring HSHeaderState['section'] in
   *  headerActions/hs.ts. Only 'register' changes anything here: the plant
   *  register doesn't report list/detail through hsView (always 'list' — see
   *  ProjectHSTab), so its primary action is gated on plantDetailOpen below
   *  instead of the hsView switch every other section uses. */
  hsSection?: 'rams' | 'toolbox' | 'plant' | 'register' | 'near-miss' | null;
  /** A machine (or a still-unsaved "New machine" draft) owns the content
   *  area — either on the standalone /plant screen or the register embedded
   *  in a project's H&S tab. Gates the native + (suppressed once true, same
   *  shape as employerDetailOpen) and takes the back chevron via
   *  PLANT_CLOSE_DETAIL_SENTINEL, ahead of hsDrillOpen so an embedded machine
   *  closes before the chevron walks up to the H&S hub. Callers derive this
   *  from PlantRegisterView's PLANT_STATE_EVENT broadcast (`!listVisible`,
   *  which is also true for the unsaved draft since selectedId is null then). */
  plantDetailOpen?: boolean;
  /** PlantRegisterView's PLANT_DETAIL_STATE_EVENT broadcast, same shape as
   *  employerDetailState — drives the native Save (savePlant), since the web
   *  header's Cancel/Save pills (buildPlantHeaderActions) are suppressed on
   *  both shells. isCreating additionally makes an untouched new-machine
   *  draft need a way to commit before any field differs from the blank
   *  baseline, and swaps the primary action's label to "Create". */
  plantDetailState?: { hasChanges: boolean; isSubmitting: boolean; isCreating: boolean } | null;
}): NativeHeaderState {
  const base = NATIVE_HEADER_CONFIG[currentView] ?? DEFAULT_HEADER;
  // Sidebar shells (macOS) have no Account screen: views whose back target
  // is 'account' (Billing...) are top-level sidebar destinations there, so a
  // back chevron would point at a view that does not exist. Workspaces and
  // Settings are the exceptions: their macOS sidebars drill in to just their
  // own items (see buildNativeNavItems), so the toolbar chevron IS the way
  // out, retargeted at Home instead of the non-existent Account.
  // Workspaces-only: drives the workspace title swap and the hierarchical
  // back target below, both of which are meaningless on any other view.
  const workspacesDrillIn = hasNativeSidebar && currentView === 'workspaces';
  // Every macOS drill-in whose sidebar shows only its own items, so the
  // toolbar chevron has to be the way out. Governs canGoBack ONLY — do not
  // hang Workspaces-specific behaviour off this.
  const macSidebarDrillIn = workspacesDrillIn
    || (hasNativeSidebar && currentView === 'system-settings')
    || (hasNativeSidebar && currentView === 'preferences')
    || (hasNativeSidebar && (currentView === 'profile' || currentView === 'account'))
    // Email is a focused workspace on macOS: its mailbox rows replace the
    // product navigation, so the native chevron is the consistent way back
    // to Home (the sidebar keeps a Home escape row too).
    || (hasNativeSidebar && currentView === 'email')
    // Sales replaced its append with a drill-in, so like Profile below it has
    // no other exit: the app nav is no longer in the sidebar to click.
    || (hasNativeSidebar && currentView === 'sales')
    // Purchases made the same move — see the buildNativeNavItems branch below.
    || (hasNativeSidebar && currentView === 'purchases')
    // Finance hubs also replace the app navigation with their own workflow
    // rows, matching the browser sidebar and avoiding two menus in one column.
    || (hasNativeSidebar && currentView === 'payroll')
    || (hasNativeSidebar && currentView === 'accounting')
    || (hasNativeSidebar && currentView === 'tax');
  const resolvedCanGoBack =
    hasNativeSidebar && (base.backTarget === 'account' || currentView === 'profile' || currentView === 'account' || currentView === 'email' || currentView === 'sales' || currentView === 'purchases' || currentView === 'payroll' || currentView === 'accounting' || currentView === 'tax')
      // Profile keeps canGoBack:false in its base config so web and iOS are
      // unchanged, but on the macOS sidebar its sections drill in and replace
      // the app nav, so the toolbar chevron has to be the way out. It resolves
      // to base.backTarget ('dashboard'), not 'account' (which also renders
      // this same screen and would half-loop).
      ? macSidebarDrillIn
      : base.canGoBack;
  // A Takeoff drawing is open: the native chevron must close the drawing
  // (back to the sheet gallery) rather than leave the project. backTarget is
  // set to a sentinel (not a real AppView) instead of leaving it empty —
  // an empty backTarget routed goBack() through window.history.back() on the
  // native side, but the app router owns window.history itself and clobbers
  // any state pushed by Takeoff, so history.back() had nothing reliable to
  // pop (especially on macOS, which has no real browser back-stack). A
  // non-empty backTarget makes native goBack() call navigate(toViewId:) with
  // this sentinel instead; AuthenticatedController's native navigate handler
  // intercepts it (before any real routing) and dispatches
  // 'takeoff:close-drawing', which TakeoffView listens for to close the
  // drawing. Restored to the normal project-detail target ('projects') as
  // soon as takeoffDrawingOpen goes false.
  // Uploader sits ABOVE an open drawing (it is a full-page overlay), so it is
  // checked first among the takeoff cases — mirrors UnifiedAppHeader treating
  // inTakeoffUploader as the highest takeoff back priority.
  // H&S drilled past its hub grid — the chevron walks the RAMS stack
  // (editor → record → list → hub) via the sentinel, one level per tap,
  // mirroring the web header's inHSDrill branch. (A machine open in the
  // embedded register takes priority over this — see plantDetailOpen's own
  // top-level branch in the return statement below, which intercepts before
  // this cascade is ever consulted for that case, standalone or embedded.)
  const hsDrillOpen = !!hsView && hsView !== 'hub';
  const resolvedBackTarget = templatePreviewOpen
    ? TEMPLATE_PREVIEW_CLOSE_SENTINEL
    : sitePhotoFullViewOpen
    ? SITE_PHOTO_CLOSE_FULL_VIEW_SENTINEL
    : emailThreadOpen
    ? EMAIL_THREAD_BACK_SENTINEL
    : interimApplicationOpen
    ? INTERIM_CLOSE_APPLICATION_SENTINEL
    : hsDrillOpen
    ? HS_BACK_SENTINEL
    : takeoffUploaderOpen
      ? (takeoffUploaderStep === 'details'
          ? TAKEOFF_UPLOADER_BACK_TO_SOURCE_SENTINEL
          : takeoffUploaderDepth > 0 ? TAKEOFF_UPLOADER_UP_SENTINEL : TAKEOFF_CLOSE_UPLOADER_SENTINEL)
      : takeoffSheetDetailsOpen
        ? TAKEOFF_CLOSE_SHEET_DETAILS_SENTINEL
        : takeoffDrawingOpen
          ? TAKEOFF_CLOSE_DRAWING_SENTINEL
          : documentsFolderOpen
            ? DOCUMENTS_FOLDER_UP_SENTINEL
            : notesDetailOpen
              ? NOTES_CLOSE_DETAIL_SENTINEL
              : reportOpen
                ? REPORTS_CLOSE_REPORT_SENTINEL
                : base.backTarget;
  const resolvedTakeoffCanGoBack =
    templatePreviewOpen || sitePhotoFullViewOpen || emailThreadOpen || interimApplicationOpen || plantDetailOpen || hsDrillOpen
      || takeoffUploaderOpen || takeoffSheetDetailsOpen || takeoffDrawingOpen || documentsFolderOpen || notesDetailOpen || reportOpen
      ? true
      : resolvedCanGoBack;

  // Phone shell: the native tab bar pins only Home / Projects / Messages /
  // Alerts, so every other destination is reached THROUGH More — and the web
  // sidebar that would otherwise be the way out is hidden underneath that bar.
  // Views configured as top-level for the desktop sidebar (Sales, Purchases,
  // Payroll, Accounting, Tax, Contacts...) therefore opened with no back
  // chevron and no exit at all. Only fills that gap: a view that already
  // declares its own back target keeps it, and the sidebar shells are
  // untouched.
  const phoneOverflowNeedsBack =
    hasNativeTabBar && !hasNativeSidebar && !PHONE_TAB_ROOT_VIEWS.has(currentView);
  const finalCanGoBack = resolvedTakeoffCanGoBack || phoneOverflowNeedsBack;
  const finalBackTarget =
    phoneOverflowNeedsBack && !base.canGoBack && resolvedBackTarget === base.backTarget
      ? 'more'
      : resolvedBackTarget;
  // A workspace is open on the macOS sidebar with unsaved edits: the web
  // header pills that normally surface Save/Cancel are suppressed in the
  // native shell, so the toolbar's primary/secondary actions are the only
  // save affordance. Suppressed to null while isSaving so a double tap
  // can't fire the save twice.
  // The workspace form's Save/Cancel bridge is shared by TWO surfaces: the
  // admin Workspaces detail AND the self-serve Organisation settings (a
  // workspace owner editing their own org). Both native shells (macOS
  // sidebar, iOS tab bar) suppress the web header pills, so the native
  // actions must fire for both views on both shells or the form is silently
  // un-saveable there.
  const workspaceSaveActive =
    (hasNativeSidebar || hasNativeTabBar)
    && (
      (currentView === 'workspaces' && !!workspaceDetail)
      || currentView === 'organisation'
      || (currentView === 'profile' && profileActiveTab?.startsWith('workspace/'))
    )
    && !!workspaceSaveState?.hasChanges;
  // The employer add/edit form is open on a shell that suppresses the web
  // header. Same reasoning as workspaceSaveActive above, with one deliberate
  // difference: this is NOT gated on hasChanges. Save is (see below), but
  // Cancel has to exist on a pristine form too — the employers route resolves
  // canGoBack to false on the sidebar shells and the form has no in-body exit,
  // so gating the whole pair on dirtiness leaves an untouched Add Employer
  // form with no way out at all.
  const employerFormActive =
    (hasNativeSidebar || hasNativeTabBar)
    && currentView === 'employers'
    && !!employerFormState
    && !employerFormState.isSubmitting;
  // 'account' renders the same UserProfile screen as 'profile'
  // (AuthenticatedWorkspace mounts it for both view ids), so every profile
  // header rule below must treat the two as one screen — checking only
  // 'profile' left the 'account' route a dead end (no chevron, no toggle).
  const isProfileScreen = currentView === 'profile' || currentView === 'account';
  const workspaceInviteActive =
    (hasNativeSidebar || hasNativeTabBar)
    && isProfileScreen
    && profileActiveTab === 'workspace/users/invite'
    && !!workspaceInviteState?.visible
    && !!workspaceInviteState.actionsInHeader;
  // Profile > Personal details staged draft. The dirty flag is already gated
  // on the details panel being open (useProfileDraft), so no tab check here.
  const profileSaveActive =
    (hasNativeSidebar || hasNativeTabBar)
    && isProfileScreen
    && profileDetailsDirty;
  const employerDetailOpen = currentView === 'employers' && !!employerDetailName;
  // The profile has no edit mode: its fields are always live and Save appears
  // only once something changed. The web header is never rendered on the shells,
  // so this single native primary slot is the only place that Save can exist
  // there — without it an employer edit could not be committed on macOS/iOS.
  const employerDetailDirty =
    employerDetailOpen && !!employerDetailState?.hasChanges && !employerDetailState.isSubmitting;
  const prospectDetailDirty =
    currentView === 'outreach' && !!prospectDetailState?.hasChanges && !prospectDetailState.saving;
  // Same shape as employerDetailDirty above, except a still-unsaved "New
  // machine" draft also counts as needing a way to commit (isCreating) even
  // before any field differs from the blank baseline — mirrors the
  // `committing` flag buildPlantHeaderActions computes for the web Save/
  // Create pill. Absent while submitting either way: native has no disabled
  // state, so hiding the action IS the disabled state.
  const plantDetailDirty =
    plantDetailOpen
    && !plantDetailState?.isSubmitting
    && (!!plantDetailState?.hasChanges || !!plantDetailState?.isCreating);
  const plannerEditorActive =
    (hasNativeSidebar || hasNativeTabBar)
    && currentView === 'project-detail'
    && projectActiveTab === 'planner'
    && !!plannerEditorState?.visible;
  const plannerDrawerActive =
    plannerEditorActive && plannerEditorState?.presentation === 'drawer';
  const plannerNativeHeaderActive =
    plannerEditorActive && plannerEditorState?.presentation === 'native-header';
  const resolvedProjectPrimaryAction: NativePrimaryActionId | null =
    plannerEditorActive
      ? (plannerNativeHeaderActive && plannerEditorState?.canEdit && plannerEditorState.canSubmit && !plannerEditorState.saving
          ? 'savePlannerActivity'
          : null)
    : workspaceInviteActive
      ? (workspaceInviteState?.canSubmit && !workspaceInviteState.submitting ? 'sendWorkspaceInvite' : null)
      : workspaceSaveActive
      ? (workspaceSaveState?.isSaving ? null : 'saveWorkspace')
      : employerFormActive
      // Native has one primary slot: Save owns it while the form is up, and
      // stays disabled-by-absence until something actually changed so a tap
      // can't submit a pristine form.
      ? (employerFormState?.hasChanges ? 'saveEmployer' : null)
      : employerDetailDirty
      // Save only exists once dirty, so a tap can never submit an untouched
      // profile — and nothing occupies the slot until the user changes something.
      ? 'saveEmployer'
      : prospectDetailDirty
      ? 'saveProspect'
      : profileSaveActive
      ? 'saveProfileChanges'
      : plantDetailDirty
      // Covers both the standalone /plant screen and the register embedded
      // in a project's H&S tab — plantDetailOpen/plantDetailState are fed
      // from the same global PLANT_STATE_EVENT/PLANT_DETAIL_STATE_EVENT
      // bridge regardless of which screen mounted PlantRegisterView, so one
      // check here covers both call sites below.
      ? 'savePlant'
      : currentView === 'employers'
      // A + over an open profile or a submitting form would unmount what the
      // user is working on. Directory only.
      ? (employersListVisible ? base.primaryAction : null)
      : currentView === 'assets'
      // Mirrors the employers branch above: + only over the table.
      // plantDetailDirty higher up already covers Save; a pristine open
      // machine (or a blank new-machine draft with nothing typed yet)
      // publishes no primary action at all.
      ? (plantDetailOpen ? null : base.primaryAction)
      : (currentView === 'profile' || currentView === 'account')
        && profileActiveTab === 'workspace/users'
        ? (workspaceUsersListVisible ? 'addUser' : null)
      // Add Workspace only makes sense on the directory itself - once a
      // workspace is open for editing (or the add form is already up), a +
      // reads as misplaced. Mirrors the web header's gate. The one exception
      // is the Users sub-tab, whose own Add User lives in the web header and
      // so has to be published here or it doesn't exist in the shells at all.
      // It sits after workspaceSaveActive deliberately: native has a single
      // primary slot, so Save wins while the workspace form is dirty and Add
      // User returns once the edit is saved or discarded.
      : currentView === 'workspaces'
        ? (workspaceDetail?.activeTab === 'users' && workspaceUsersListVisible
            ? 'addUser'
            : workspaceDetail?.activeTab === 'documents'
            ? 'uploadDocument'
            : workspaceDetail || workspacesActiveTab === 'add-client' ? null : base.primaryAction)
        : currentView === 'project-detail'
        ? projectActiveTab === 'documents'
          ? 'uploadDocument'
          : projectActiveTab === 'pricing'
            ? 'add'
            : projectActiveTab === 'tasks'
              ? null
            : projectActiveTab === 'planner'
              ? (canEditPlanner ? 'add' : null)
            : projectActiveTab === 'interim'
              ? (canManageApplications ? 'add' : null)
              // H&S: + creates a RAMS on the list; on an open record the
              // primary slot carries the single status-advancing action
              // (mirrors the web header's progress pill). Draft's Send for
              // review is suppressed rather than disabled while incomplete —
              // native has no disabled state, absence is the signal.
              // Approving a submitted record is the employer's action on the
              // public review page, so 'submitted' carries no primary action.
              : projectActiveTab === 'hs'
                ? (hsSection === 'register'
                    // The register doesn't report list/detail through hsView
                    // (always 'list' — see hsSection's doc comment above), so
                    // gate on plantDetailOpen instead, same as the standalone
                    // /plant branch above. plantDetailDirty higher up already
                    // covers Save.
                    ? (plantDetailOpen ? null : 'add')
                    : hsView === 'list'
                      ? 'add'
                      : hsView === 'record'
                        ? (hsStatus === 'draft'
                            ? (hsCanIssue ? 'hsSubmit' : null)
                            : hsStatus === 'approved' ? 'hsIssue' : null)
                        : null)
                : null
        : currentView === 'projects' && !canAddProject
          ? null
          : base.primaryAction;
  // Every primary action a project tab publishes creates or saves something.
  const projectWritesBlocked = projectReadOnly && currentView === 'project-detail';
  const resolvedPrimaryAction = projectWritesBlocked ? null : resolvedProjectPrimaryAction;
  const primaryActionMeta = resolvedPrimaryAction
    ? NATIVE_PRIMARY_ACTIONS[resolvedPrimaryAction]
    : null;
  // A still-unsaved "New machine" draft reads "Create" rather than "Save" —
  // the same split PlantDetailState documents for the web pill
  // (buildPlantHeaderActions). Overriding the static meta's label here is
  // cheaper than a second NativePrimaryActionId that would only exist to
  // carry a different word over an identical checkmark icon.
  const resolvedPrimaryActionLabel =
    resolvedPrimaryAction === 'savePlant' && plantDetailState?.isCreating
      ? 'Create'
      : primaryActionMeta?.label ?? null;
  // Cancel rides the generic secondaryAction pass-through (same mechanism
  // UserProfile.tsx uses directly) rather than a new bespoke field. No other
  // view feeds secondaryAction through this central call today, so this
  // can't clobber a child-owned secondary action.
  // An open RAMS record on a shell: the record's lifecycle actions live in
  // the anchored toolbar menu (the web header's Edit/New version/Recall to
  // draft/overflow pills are all suppressed there). Item taps ride
  // onHeaderControl with these hs-menu:* ids — AuthenticatedController maps
  // them onto the same hs:* window events the web pills fire.
  const hsRecordOpen =
    currentView === 'project-detail' && projectActiveTab === 'hs'
    && hsView === 'record' && !!hsStatus;
  // Branches by hsSection because the item list below is built entirely
  // from RamsStatus literals — a Toolbox Talk ('draft'/'delivered') or Plant
  // Check ('draft'/'completed') record matched 'draft' by coincidence (both
  // show Edit) but otherwise built the wrong menu: no lifecycle action for
  // their own lifecycle, and an unconditional "Download RAMS pack" item that
  // dispatches an event nothing outside a RAMS record listens for. Mirrors
  // headerActions/hs.ts's own per-section split for the web pills.
  const allHsMenuItems: NativeHeaderState['secondaryMenuItems'] = !hsRecordOpen
    ? undefined
    : hsSection === 'toolbox'
      ? [
          // Edit — always available, same as the web pill: a toolbox talk has
          // no controlled-document edit lock (hs_toolbox_talk_routes.tsx's PUT
          // accepts any status).
          { id: 'hs-menu:toolbox-edit', label: 'Edit', sfSymbol: 'pencil' },
          ...(hsStatus === 'draft'
            ? [{ id: 'hs-menu:toolbox-deliver', label: 'Mark delivered', sfSymbol: 'checkmark.circle' }]
            : []),
          { id: 'hs-menu:toolbox-download', label: 'Download sign-in sheet', sfSymbol: 'arrow.down.doc' },
          ...(hsStatus === 'delivered'
            ? [{ id: 'hs-menu:toolbox-undeliver', label: 'Back to draft', sfSymbol: 'arrow.uturn.backward' }]
            : []),
          { id: 'hs-menu:toolbox-delete', label: 'Delete', sfSymbol: 'trash', destructive: true },
        ]
      : hsSection === 'plant'
        ? [
            // Edit — always available, same as Toolbox Talks: a plant check
            // has no controlled-document edit lock either.
            { id: 'hs-menu:plant-edit', label: 'Edit', sfSymbol: 'pencil' },
            ...(hsStatus === 'draft'
              ? [{ id: 'hs-menu:plant-complete', label: 'Mark complete', sfSymbol: 'checkmark.circle' }]
              : []),
            { id: 'hs-menu:plant-download', label: 'Download PDF', sfSymbol: 'arrow.down.doc' },
            ...(hsStatus === 'completed'
              ? [
                  { id: 'hs-menu:plant-reopen', label: 'Reopen', sfSymbol: 'arrow.uturn.backward' },
                  { id: 'hs-menu:plant-save-template', label: 'Save as template', sfSymbol: 'bookmark' },
                ]
              : []),
            { id: 'hs-menu:plant-delete', label: 'Delete', sfSymbol: 'trash', destructive: true },
          ]
        : hsSection === 'near-miss'
          ? [
              // Edit — always available, same as Toolbox Talks/Plant Checks: a
              // near miss report has no controlled-document edit lock either.
              { id: 'hs-menu:near-miss-edit', label: 'Edit', sfSymbol: 'pencil' },
              ...(hsStatus === 'open'
                ? [{ id: 'hs-menu:near-miss-close', label: 'Mark closed', sfSymbol: 'checkmark.circle' }]
                : []),
              ...(hsStatus === 'closed'
                ? [{ id: 'hs-menu:near-miss-reopen', label: 'Reopen', sfSymbol: 'arrow.uturn.backward' }]
                : []),
              { id: 'hs-menu:near-miss-delete', label: 'Delete', sfSymbol: 'trash', destructive: true },
            ]
          : [
            ...(hsStatus === 'draft'
              ? [{ id: 'hs-menu:edit', label: 'Edit', sfSymbol: 'pencil' }]
              : []),
            ...(hsStatus === 'issued' || hsStatus === 'approved' || hsStatus === 'archived'
              ? [{ id: 'hs-menu:new-version', label: 'New version', sfSymbol: 'doc.badge.plus' }]
              : []),
            { id: 'hs-menu:download-pdf', label: 'Download RAMS pack', sfSymbol: 'arrow.down.doc' },
            // Recall to draft — submitted only. Same id shape as the 'archived'
            // restore below (hs-menu:transition:draft); AuthenticatedController's
            // generic 'hs-menu:transition:*' handler already covers it, no new
            // branch needed. A submitted record has no in-app edit path (PUT is
            // draft-only), so this is how a contractor pulls a mistake back to
            // fix and resubmit — submitted->draft is already a legal transition.
            ...(hsStatus === 'submitted'
              ? [{ id: 'hs-menu:transition:draft', label: 'Recall to draft', sfSymbol: 'arrow.uturn.backward' }]
              : []),
            ...(hsStatus === 'approved' || hsStatus === 'issued'
              ? [{ id: 'hs-menu:save-template', label: 'Save as template', sfSymbol: 'bookmark' }]
              : []),
            // Archive is issued-only: RAMS_ALLOWED_TRANSITIONS (ramsUtils.ts)
            // doesn't allow approved->archived — an approved record goes
            // forward to issued or back to draft, never straight to archived.
            ...(hsStatus === 'issued'
              ? [{ id: 'hs-menu:transition:archived', label: 'Archive', sfSymbol: 'archivebox' }]
              : []),
            ...(hsStatus === 'archived'
              ? [{ id: 'hs-menu:transition:draft', label: 'Restore to draft', sfSymbol: 'arrow.uturn.backward' }]
              : []),
            ...(hsStatus === 'draft' || hsStatus === 'archived'
              ? [{ id: 'hs-menu:delete', label: 'Delete', sfSymbol: 'trash', destructive: true }]
              : []),
          ];
  const hsMenuItems = projectWritesBlocked
    ? allHsMenuItems?.filter((item) => !isProjectWriteAction(item.id))
    : allHsMenuItems;
  const projectNavButtons = projectWritesBlocked
    ? navButtons?.filter((button) => !isProjectWriteAction(button.id))
    : navButtons;
  const resolvedSecondaryAction = workspaceInviteActive
    ? (workspaceInviteState?.submitting ? null : 'workspace-invite-cancel')
    : workspaceSaveActive
    ? (workspaceSaveState?.isSaving ? null : 'workspace-cancel')
    : employerFormActive
      ? 'employer-cancel'
      : employerDetailDirty
        ? 'employer-detail-cancel'
      : prospectDetailDirty
        ? 'prospect-detail-cancel'
        : profileSaveActive
          ? 'cancelProfileChanges'
        : hsRecordOpen
          ? 'hs-menu'
          : secondaryAction;
  const resolvedSecondaryActionLabel = workspaceInviteActive
    ? (workspaceInviteState?.submitting ? null : 'Cancel')
    : workspaceSaveActive
    ? (workspaceSaveState?.isSaving ? null : 'Cancel')
    : employerFormActive
      ? 'Cancel'
      : employerDetailDirty || prospectDetailDirty
        ? 'Cancel'
        : profileSaveActive
          ? 'Cancel'
        : hsRecordOpen
          ? 'More'
          : secondaryActionLabel;
  const resolvedSecondaryActionSymbol = workspaceInviteActive
    ? (workspaceInviteState?.submitting ? null : 'xmark')
    : workspaceSaveActive
    ? (workspaceSaveState?.isSaving ? null : 'xmark')
    : employerFormActive
      ? 'xmark'
      : employerDetailDirty || prospectDetailDirty
        ? 'xmark'
        : profileSaveActive
          ? 'xmark'
        : hsRecordOpen
          ? 'ellipsis.circle'
          : secondaryActionSymbol;

  // The Messages back button is owned dynamically by the WEB: MessagesView sets
  // canGoBack:false on the chat list, ChatWindow sets canGoBack:true inside a
  // chat thread. This central parent effect re-fires on unrelated deps (unread
  // counts, presence, tab state) WHILE a chat is open — and because the native
  // shell MERGES header updates, emitting the config's canGoBack:false here
  // clobbers ChatWindow's true, so the back button vanishes and the user gets
  // stuck in the chat until ChatWindow next resends. Omit canGoBack/backTarget
  // for 'messages' so the child's value is preserved (absent field = no-op).
  const viewOwnsHeaderNav = currentView === 'messages';
  const { canGoBack: _omitCanGoBack, backTarget: _omitBackTarget, ...baseRest } = base;
  // Workspace detail open on the macOS sidebar: the toolbar title swaps to
  // the workspace name (mirrors client-detail's selectedClientName pattern)
  // so the user reads which workspace they're in without a breadcrumb row.
  // Same treatment for the Employers drill-in and its form: the route's own
  // title is the directory's, so without this both a profile and the add form
  // render under a "Employers" title that describes neither.
  const resolvedTitle =
    plannerNativeHeaderActive
      ? (!plannerEditorState?.canEdit
          ? 'Activity details'
          : plannerEditorState.editing ? 'Edit activity' : 'New activity')
      : workspacesDrillIn && workspaceDetail
      ? workspaceDetail.clientName
      : currentView === 'employers' && employerFormState
        ? (employerFormState.isEdit ? 'Edit Employer' : 'Add Employer')
        : employerDetailOpen
          ? employerDetailName!
          : title;

  return {
    title: resolvedTitle,
    ...(subtitle !== undefined ? { subtitle } : {}),
    // The iPhone shell's only route into a project's own sections: it hides
    // the web header AND the web bottom nav, and its four tabs stay global,
    // so nothing else there reaches them. Opens the web's offcanvas drawer
    // rather than a native panel, so the phone matches mobile web exactly.
    // Any view whose sections the tab-bar shell publishes into navItems: a
    // project's toolbox, and Profile's panels. Both are sidebar-driven on the
    // other shells, and a phone has no sidebar. Profile shows it on Overview
    // only — its sections are drill-ins now (the Account list navigates, and
    // the chevron below walks back), so a hamburger beside the back chevron
    // would put two leading controls on a section page.
    // Supporting shells use this same drawer for finance sections; their
    // in-page chip row is suppressed behind the matching capability gate.
    showSectionsToggle:
      hasNativeTabBar &&
      !plannerEditorActive &&
      !emailThreadOpen &&
      !sitePhotoFullViewOpen &&
      (currentView === 'project-detail' ||
        (hasNativeSectionMenu() && ['sales', 'purchases', 'payroll', 'accounting', 'tax'].includes(currentView)) ||
        (isProfileScreen && (!profileActiveTab || profileActiveTab === 'overview'))),
    // Which row the shell's section menu should mark as current. Prefixed to
    // match the ids published in navItems, so the menu can compare directly.
    activeProjectSectionId:
      currentView === 'project-detail' && projectActiveTab
        ? `${PROJECT_TAB_PREFIX}${isProjectReportsTab(projectActiveTab) ? 'reports' : projectActiveTab}`
        : isProfileScreen && profileActiveTab
          ? `${PROFILE_TAB_PREFIX}${profileActiveTab}`
          : undefined,
    activeProjectId: currentView === 'project-detail' ? activeProjectId : null,
    activeProjectName: currentView === 'project-detail' ? activeProjectName : null,
    ...(viewOwnsHeaderNav
      ? baseRest
      // The export-template preview owns the chevron while it is open: it
      // closes the preview back to the template library rather than leaving
      // Settings. Must be its own branch — the project-detail branch below is
      // the only other consumer of resolvedBackTarget.
      : templatePreviewOpen
        ? { ...base, canGoBack: true, backTarget: TEMPLATE_PREVIEW_CLOSE_SENTINEL }
      // A drilled-in employer profile: the chevron closes the profile rather
      // than leaving Employers. Employers' own backTarget is 'account', which
      // resolves to no chevron at all on the sidebar shells, so this is the
      // only exit the profile has there.
      : employerDetailOpen
        ? { ...base, canGoBack: true, backTarget: EMPLOYERS_CLOSE_DETAIL_SENTINEL }
      // A drilled-in machine (standalone /plant, or the register embedded in
      // a project's H&S tab): the chevron closes it back to the table rather
      // than leaving whichever screen mounted PlantRegisterView. Not gated on
      // currentView, so one branch covers both — and, checked ahead of the
      // project-detail branch below, it takes priority over hsDrillOpen: an
      // open machine embedded in the register closes on the first tap, and a
      // second walks the H&S stack back to the hub (hsView stays 'list' the
      // whole time the register is open — see hsSection's doc comment — so
      // hsDrillOpen alone can't tell an open machine from the bare list).
      : plantDetailOpen
        ? { ...base, canGoBack: true, backTarget: PLANT_CLOSE_DETAIL_SENTINEL }
      : workspaceDocumentsFolderOpen
        ? { ...base, canGoBack: true, backTarget: DOCUMENTS_FOLDER_UP_SENTINEL }
      // An open rate item form: the chevron closes the form back to the library
      // list. Gated on the view as well as the flag so a stale broadcast from a
      // form that has already unmounted cannot hand any other view a chevron.
      // Resource Library's own backTarget is 'account', which resolves to no
      // chevron at all on the sidebar shells, so this is the form's only exit
      // there, the same dead end the employer profile above had.
      : currentView === 'resource-library' && resourceLibraryItemFormOpen
        ? { ...base, canGoBack: true, backTarget: RESOURCE_LIBRARY_ITEM_BACK_SENTINEL }
      : currentView === 'email' && emailThreadOpen
        ? { ...base, canGoBack: true, backTarget: EMAIL_THREAD_BACK_SENTINEL }
      : plannerDrawerActive
        ? { ...base, canGoBack: false, backTarget: '' }
      : plannerNativeHeaderActive
        ? { ...base, canGoBack: true, backTarget: PLANNER_CLOSE_EDITOR_SENTINEL }
      : currentView === 'project-detail' && (sitePhotoFullViewOpen || emailThreadOpen || interimApplicationOpen || hsDrillOpen || takeoffUploaderOpen || takeoffSheetDetailsOpen || takeoffDrawingOpen || documentsFolderOpen || notesDetailOpen || reportOpen)
        ? { ...base, canGoBack: finalCanGoBack, backTarget: finalBackTarget }
      // Profile sections on the iPhone shell are drill-ins from Overview (the
      // Account list rows navigate into them), so the chevron walks back to
      // Overview rather than leaving Profile. The tab-prefixed target rides
      // the existing navigate(toViewId:) path, whose PROFILE_TAB_PREFIX
      // branch restores the section state. iPad/macOS keep their sidebar
      // behaviour (macSidebarDrillIn) — the sections are always in reach
      // there, so back-to-Overview would just duplicate the sidebar.
      : isProfileScreen && hasNativeTabBar && !hasNativeSidebar && !!profileActiveTab && profileActiveTab !== 'overview'
        ? { ...base, canGoBack: true, backTarget: `${PROFILE_TAB_PREFIX}overview` }
      // Preferences panes on the iPhone shell are drill-ins from the section
      // list, so the chevron returns to the list; sidebar shells list the
      // sections beside the pane and keep the exit to Home.
      : currentView === 'preferences' && hasNativeTabBar && !hasNativeSidebar && !!preferencesSection
        ? { ...base, canGoBack: true, backTarget: `${PREFERENCES_TAB_PREFIX}${PREFERENCES_LIST_ID}` }
        : {
            ...base,
            // finalCanGoBack, not resolvedCanGoBack: this is the branch every
            // ordinary view takes, so the phone-overflow rule has to be applied
            // HERE or it reaches only the takeoff/project-detail branch above —
            // which is exactly why Contacts still opened with no chevron.
            canGoBack: finalCanGoBack,
            // Hierarchical back inside the Workspaces drill-in: an open
            // workspace detail closes back to the Directory list first (via
            // the WORKSPACES_CLOSE_DETAIL_SENTINEL, same pattern as Takeoff's
            // drawing-close sentinel above); otherwise sub-tabs (Requests,
            // Add Workspace) return to the clients list, and only the list
            // itself exits to Home. The tab-prefixed target rides the
            // existing navigate(toViewId:) path, whose WORKSPACES_TAB_PREFIX
            // branch restores the list tab.
            backTarget: workspacesDrillIn
              ? workspaceDetail
                ? WORKSPACES_CLOSE_DETAIL_SENTINEL
                : workspacesActiveTab && workspacesActiveTab !== 'clients'
                  ? `${WORKSPACES_TAB_PREFIX}clients`
                  : 'dashboard'
              // Any other macOS drill-in (Settings) simply exits to Home —
              // inheriting the Workspaces hierarchy above sent Settings into
              // the Workspaces directory off a stale workspacesActiveTab.
              : macSidebarDrillIn
                ? 'dashboard'
                // Phone overflow: reached through More, so More is the way
                // out. Covers 'account' too — Workspaces, Employers and
                // Resource Library declare canGoBack with backTarget
                // 'account', which is right on the sidebar shells but on a
                // phone dropped the user on Account History, a screen they
                // never came from.
                : phoneOverflowNeedsBack
                  && (!base.canGoBack || base.backTarget === 'account')
                  ? 'more'
                  : base.backTarget,
          }),
    // Employers publishes showLargeTitle:true for the directory. A full-page
    // form and a profile are not the directory, and a 34pt title over either
    // reads as the wrong screen — the same suppression the web header gets
    // from pushBackAction's `back-` id prefix.
    ...(currentView === 'employers' && (employerFormState || employerDetailOpen)
      ? { showLargeTitle: false }
      : {}),
    primaryAction: resolvedPrimaryAction,
    primaryActionLabel: resolvedPrimaryActionLabel,
    primaryActionSymbol: primaryActionMeta?.sfSymbol ?? null,
    // A pushed, native-header editor is a detail and may hide the phone tab
    // bar. The iPhone Planner drawer is different: hiding the bar tears down
    // IOSWebShellTabBarController while the web backdrop is still mounted,
    // exposing the underlying Projects surface and making the app appear
    // frozen. Keep native chrome structurally alive beneath bottom drawers.
    hideTabBar: plannerNativeHeaderActive || emailThreadOpen,
    // Only the dashboard renders a web large title today, so only it tells native
    // to blank the inline centre title until scrolled. Widen this as other views
    // gain a web large title.
    //
    // iPhone only. On iPad the web stopped drawing that title (DashboardHome
    // gates it on hasNativeTabBar) because the real UIKit bar there should own
    // it — so blanking the bar's title on iPad would leave the screen with no
    // title at all rather than deferring to a web one.
    largeTitle: currentView === 'dashboard' && !hasNativeSidebar,
    // Caller-supplied navButtons (e.g. macOS global Messages + Notifications).
    // Only spreads when explicitly provided; undefined leaves the field absent
    // so per-view overrides (ChatWindow, CalendarPage, etc.) are not clobbered.
    ...((currentView === 'profile' || currentView === 'account') && profileSignatureState
      ? { navButtons: [...signatureNativeButtons(profileSignatureState), ...(navButtons ?? [])] }
      : projectNavButtons !== undefined ? { navButtons: projectNavButtons } : {}),
    ...(leadingNavButtons !== undefined ? { leadingNavButtons } : {}),
    // Caller-supplied anchored toolbar menu (e.g. the Enquiries Sort & Filter
    // menu). Same absent-when-undefined rule so ChatWindow's own ⋯ menu, which
    // it sends from its child effect, is never overwritten by this parent.
    ...(resolvedSecondaryAction !== undefined ? { secondaryAction: resolvedSecondaryAction } : {}),
    ...(resolvedSecondaryActionLabel !== undefined ? { secondaryActionLabel: resolvedSecondaryActionLabel } : {}),
    ...(resolvedSecondaryActionSymbol !== undefined ? { secondaryActionSymbol: resolvedSecondaryActionSymbol } : {}),
    ...(hsMenuItems !== undefined
      ? { secondaryMenuItems: hsMenuItems }
      : secondaryMenuItems !== undefined ? { secondaryMenuItems } : {}),
    // Toolbar picker: the BOQ bill/summary switcher, the Site Photos view
    // switcher and the Resource Library picker. Their web header is suppressed
    // in the Apple shells.
    // Every other view sends null so the native shell clears a stale switcher.
    // viewSwitcher: null explicitly clears; undefined leaves field absent (no-op).
    ...((currentView === 'project-detail' && (projectActiveTab === 'pricing' || projectActiveTab === 'site_photos'))
      || currentView === 'resource-library' || isPipelineDestination(currentView) || currentView === 'outreach' || isAccountingDestination(currentView)
      ? { viewSwitcher: viewSwitcher ?? null }
      : { viewSwitcher: null }),
  };
}

export function isNativeContextualSearchRoute({
  currentView,
  projectActiveTab,
  employersListVisible = true,
  workspaceDocumentsOpen = false,
}: {
  currentView: AppView;
  projectActiveTab?: string | null;
  /** The workspace file browser is showing (under a company or Profile); its own search provider takes the query. */
  workspaceDocumentsOpen?: boolean;
  /** Employers only searches its directory. Defaults true so callers that
   *  don't observe the screen's state keep the old behaviour, but every real
   *  call site passes it — a search bar over the add form or a profile is the
   *  exact desync the state channel exists to prevent. */
  employersListVisible?: boolean;
}): boolean {
  // Checked before the list below: 'employers' is in it, so the includes()
  // would otherwise short-circuit this to true.
  if (currentView === 'employers') return employersListVisible;

  const nativeSearchablePages: AppView[] = [
    // Email owns a contextual provider in EmailView. The Apple shell should
    // render its system search field rather than leaving the web SearchPill
    // beneath the native toolbar.
    'email',
    'messages',
    'projects',
    'tasks',
    'system-settings',
    'user-management',
    'workspaces',
    'employers',
    'supervisor-management',
    'pipeline',
    // The rate-item search box lives in the web header, which every Apple
    // shell replaces, so native search is the only way to filter the library
    // there. LibraryItemsPane registers a contextual search provider to
    // receive the query, since there is no DOM field for the usual fallback
    // to type into.
    'resource-library',
    // Sales hub Invoices section — same reasoning as resource-library above.
    // Gated on the whole hub rather than just the invoices section: unlike
    // employersListVisible / workspaceDetailActiveTab, there is no per-section
    // flag threaded into this call for Sales, so the bar shows across every
    // Sales section. FinanceBilling's registered provider is only enabled
    // while the invoices section is open, so typing elsewhere in Sales is a
    // silent no-op rather than acting on the wrong list.
    'sales',
  ];

  return nativeSearchablePages.includes(currentView) || workspaceDocumentsOpen ||
    (currentView === 'project-detail' &&
      (projectActiveTab === 'documents' || projectActiveTab === 'takeoff' || projectActiveTab === 'notes' || projectActiveTab === 'email' || projectActiveTab === 'reports'));
}

/**
 * Placeholder for the native search field per route, so the iOS `.searchable`
 * bar reads e.g. "Search projects" / "Search conversations" instead of a
 * generic "Search". Pairs with `isNativeContextualSearchRoute`.
 */
export function nativeContextualSearchPrompt({
  currentView,
  projectActiveTab,
  takeoffDrawingOpen = false,
  workspaceDetailActiveTab = null,
  documentsFolderName = null,
  workspaceDocumentsOpen = false,
}: {
  currentView: AppView;
  workspaceDocumentsOpen?: boolean;
  projectActiveTab?: string | null;
  /** Active section of an open workspace. The workspaces route hosts several
   *  sub-tabs whose search targets differ, so the prompt can't come from
   *  currentView alone — on Users it would otherwise read "Search workspaces"
   *  over a field that filters the users table. */
  workspaceDetailActiveTab?: string | null;
  /** True while a takeoff drawing is open (vs. the sheet gallery) — the
   *  search box's behaviour flips from filtering the sheet gallery by name
   *  to searching the open drawing's PDF text layer, so the prompt should
   *  say so. See TakeoffPreviewCanvas's in-drawing text search. */
  takeoffDrawingOpen?: boolean;
  /** Open documents folder, broadcast on `documents:folder-state-change`.
   *  Null at the tab's root, where there is no folder to name. */
  documentsFolderName?: string | null;
}): string {
  // Name the open folder, so the field says what it will actually search
  // rather than a fixed "Search documents" that never changes as you drill in.
  if (currentView === 'project-detail' && projectActiveTab === 'documents') {
    return documentsFolderName ? `Search ${documentsFolderName}` : 'Search documents';
  }
  if (currentView === 'project-detail' && projectActiveTab === 'notes') {
    return 'Search notes';
  }
  if (currentView === 'project-detail' && projectActiveTab === 'takeoff') {
    return takeoffDrawingOpen ? 'Search drawing' : 'Search sheets';
  }
  if (currentView === 'project-detail' && projectActiveTab === 'reports') {
    return 'Search reports';
  }
  if (currentView === 'project-detail' && projectActiveTab === 'email') {
    return 'Search project mail';
  }
  if (workspaceDocumentsOpen) return documentsFolderName ? `Search ${documentsFolderName}` : 'Search documents';
  if (currentView === 'workspaces' && workspaceDetailActiveTab === 'users') return 'Search users';
  switch (currentView) {
    case 'email': return 'Search email';
    case 'pipeline': return 'Search deals';
    case 'resource-library': return 'Search rate items';
    case 'projects': return 'Search projects';
    case 'messages': return 'Search conversations';
    case 'tasks': return 'Search tasks';
    case 'employers': return 'Search employers';
    case 'user-management': return 'Search users';
    case 'workspaces': return 'Search workspaces';
    case 'supervisor-management': return 'Search supervisors';
    case 'system-settings': return 'Search settings';
    case 'sales': return 'Search invoices';
    default: return 'Search';
  }
}

export const PIPELINE_SECTION_PREFIX = 'pipeline-section:';

/** Apple toolbar menus already transport viewSwitcher option ids. Share raw
 * grant/role filtering with the web hub without needing a new native drawer. */
export function buildNativePipelineNavigation({ role, currentView, outreachActiveTab, resolvedMainItems, internalWorkspace = false }: {
  role: string;
  currentView: AppView;
  outreachActiveTab?: string | null;
  internalWorkspace?: boolean;
  resolvedMainItems?: readonly WorkspaceNavigationItem[] | null;
}) {
  const groups = getSidebarNavGroups({ role, internalWorkspace, resolvedItems: resolvedMainItems });
  const outreachChoices = outreachMenuItems(role, internalWorkspace, groups.menuGrants);
  const underOutreach = currentView === 'outreach' || (currentView === 'pipeline' && outreachChoices.some(item => item.id === 'enquiries'));
  const primaryItemId = underOutreach ? groups.mainNavItems.find(item => item.id === 'outreach')?.id ?? null : groups.mainNavItems.find(item => isPipelineDestination(item.id))?.id ?? null;
  const choices = underOutreach ? outreachChoices : pipelineMenuItems(role, internalWorkspace, groups.menuGrants);
  const options = choices.map(item => ({
    id: item.destination === 'outreach' ? `${PIPELINE_SECTION_PREFIX}outreach:${item.id}` : `${PIPELINE_SECTION_PREFIX}${item.destination}`,
    label: item.label,
  }));
  const selectedId = currentView === 'outreach'
    ? `${PIPELINE_SECTION_PREFIX}outreach:${isOutreachSectionId(outreachActiveTab) ? outreachActiveTab : 'approvals'}`
    : `${PIPELINE_SECTION_PREFIX}${currentView}`;
  return {
    primaryItemId,
    viewSwitcher: (isPipelineDestination(currentView) || currentView === 'outreach') && options.length > 0 ? {
      options, selectedId: options.some(option => option.id === selectedId) ? selectedId : options[0].id,
      style: 'menu' as const,
    } : null,
  };
}

/** Resolve only an option in the currently authorized picker. Old or forged
 * native option ids cannot widen access beyond the underlying raw grants. */
export function nativePipelineNavigationTarget(id: string, navigation: ReturnType<typeof buildNativePipelineNavigation>): {
  view: AppView; sectionId?: string;
} | null {
  if (!navigation.viewSwitcher?.options.some(option => option.id === id)) return null;
  const target = id.slice(PIPELINE_SECTION_PREFIX.length);
  if (target.startsWith('outreach:')) {
    const sectionId = target.slice('outreach:'.length);
    return isOutreachSectionId(sectionId) ? { view: 'outreach', sectionId } : null;
  }
  return isPipelineDestination(target) ? { view: target } : null;
}

export const ACCOUNTING_AREA_PREFIX = 'accounting-area:';

export function buildNativeAccountingNavigation({ role, currentView, resolvedMainItems }: {
  role: string; currentView: AppView; resolvedMainItems?: readonly WorkspaceNavigationItem[] | null;
}) {
  const groups = getSidebarNavGroups({ role, resolvedItems: resolvedMainItems });
  const options = accountingAreaItems(groups.accountingDestinations).map(({ id, label }) => ({
    id: `${ACCOUNTING_AREA_PREFIX}${id}`, label,
  }));
  return {
    primaryItemId: groups.mainNavItems.find(item => isAccountingDestination(item.id))?.id ?? null,
    viewSwitcher: isAccountingDestination(currentView) && options.length ? {
      options, selectedId: options.some(option => option.id === `${ACCOUNTING_AREA_PREFIX}${currentView}`)
        ? `${ACCOUNTING_AREA_PREFIX}${currentView}` : options[0].id, style: 'menu' as const,
    } : null,
  };
}

export function nativeAccountingNavigationTarget(id: string, navigation: ReturnType<typeof buildNativeAccountingNavigation>) {
  if (!navigation.viewSwitcher?.options.some(option => option.id === id)) return null;
  const view = id.slice(ACCOUNTING_AREA_PREFIX.length);
  return isAccountingDestination(view) ? view : null;
}

function buildBaseNativeNavItems(role: string, resolvedItems?: readonly WorkspaceNavigationItem[] | null, internalWorkspace = false) {
  const isAdmin = role === 'super_admin' || role === 'admin';
  const isStaff = role === 'internal' || role === 'staff' || isAdmin;
  const isClient =
    role === 'workspace_owner' || role === 'workspace_admin' || role === 'workspace_user' ||
    role === 'member'          || role === 'client';
  const isSupervisor = role === 'workspace_user' || role === 'supervisor';
  const items = buildNativeSidebarNavItems({ role, resolvedItems, internalWorkspace });

  if (isStaff || isClient || isSupervisor) {
    items.push({
      id: 'profile',
      label: 'Profile',
      sfSymbol: SF.profile ?? 'person.circle.fill',
      section: 'main',
    });
  }

  return items;
}

export function buildNativeNavItems({
  role,
  currentView,
  projectActiveTab,
  projectTabProps,
  projectPermissions,
  workspacesStats,
  workspaceDetail,
  hasNativeSidebar = false,
  hasNativeTabBar = false,
  interimApplicationOpen = false,
  hsEditorSectionIds = null,
  hsDocumentSectionIds = null,
  userFormOpen = false,
  employerDetailOpen = false,
  employerFormSectionIds = null,
  emailSharedMailboxes = [],
  canManageWorkspaceSettings = false,
  canViewWorkspacePlugins = false,
  resolvedMainItems,
  resolvedProjectItems,
  internalWorkspace = false,
}: {
  internalWorkspace?: boolean;
  role: string;
  resolvedMainItems?: readonly WorkspaceNavigationItem[] | null;
  resolvedProjectItems?: readonly WorkspaceNavigationItem[] | null;
  currentView: AppView;
  projectActiveTab?: string | null;
  projectTabProps: {
    isClient: boolean;
    isEstimatingPhase: boolean;
    activeTasksCount: number;
    activeClaimsCount?: number;
    isStaff: boolean;
    projectId?: string;
    projectName?: string;
    projectStage?: string;
    internalUserIds?: string[];
    runsTender?: boolean;
    issuesContractDocuments?: boolean;
    applicationWorkflow?: import('../contract-administration/types').ApplicationWorkflow;
    canGivePayLessNotice?: boolean;
  } | null;
  projectPermissions: any;
  workspacesStats: { pendingRequests: number } | null;
  /** An interim application is open inside the Applications tab - drills the
   *  sidebar in to the application's sections (INTERIM_SECTION_PREFIX). */
  interimApplicationOpen?: boolean;
  /** Visible sections from an open RAMS editor. When present on a macOS
   *  project sidebar, these replace the project tool list. */
  hsEditorSectionIds?: readonly string[] | null;
  /** Visible sections from a read-only RAMS record, independently of editor state. */
  hsDocumentSectionIds?: readonly string[] | null;
  /** The edit-user form is open inside the workspace Users tab — drills the
   *  sidebar one level past the workspace sections to the form's own
   *  (USER_FORM_SECTION_PREFIX). */
  userFormOpen?: boolean;
  /** An employer profile is open — drills the sidebar in to the profile's own
   *  sections (EMPLOYER_DETAIL_TAB_PREFIX) instead of the app nav, matching
   *  what the web sidebar does. */
  employerDetailOpen?: boolean;
  /** The employer add/edit form's currently visible pages, or null when no form
   *  is open. An array rather than a boolean because the form's section list is
   *  conditional (Credit Check comes and goes), so the rows have to be built
   *  from what the form says exists. */
  employerFormSectionIds?: readonly string[] | null;
  /** Shared mailboxes the signed-in user belongs to. */
  emailSharedMailboxes?: readonly { id: string; label: string }[];
  /** Active-workspace owner/admin gate for workspace panels merged into Profile. */
  canManageWorkspaceSettings?: boolean;
  /** Same catalogue visibility gate used by the web profile sidebar. */
  canViewWorkspacePlugins?: boolean;
  /** Open workspace on lg+ (WorkspaceList's 'workspaces-list:detail-change'
   *  broadcast; null when the directory list is showing). When set, the
   *  macOS sidebar drills further in to this workspace's own section nav
   *  instead of the Directory/Requests/Add Workspace list. */
  workspaceDetail?: { clientId: string; clientName: string; activeTab: string } | null;
  hasNativeSidebar?: boolean;
  hasNativeTabBar?: boolean;
}) {
  const baseItems = buildBaseNativeNavItems(role, resolvedMainItems, internalWorkspace);
  const includeOutreachTemplates = outreachMenuItems(role, internalWorkspace, (resolvedMainItems ?? []).map(item => item.id)).length > 0;
  const isAdmin = role === 'super_admin' || role === 'admin';

  /** The open project's own sections, as native nav items.
   *
   *  buildProjectSidebarTabItems interleaves kind:'heading' rows to group the
   *  tabs (Commercial / Delivery / Records / Manage); those fold into the native
   *  `section` so headings drive grouping instead of rendering as tappable rows.
   *  'main' is the header-less bucket, so the landing Overview sits at the top
   *  rather than under a lonely one-item header.
   *
   *  Shared by the sidebar shells and the iPhone tab bar, which reaches these
   *  through its overflow menu — see the project branch inside `hasNativeTabBar`. */
  const projectSectionItems = () => {
    const items: { id: string; label: string; sfSymbol?: string; section: string }[] = [];
    let section = 'main';
    const sections = buildProjectSidebarTabItems(
      { activeClaimsCount: 0, ...projectTabProps! }, projectPermissions, resolvedProjectItems, projectActiveTab,
    );
    for (const item of sections) {
      if (item.kind === 'heading') {
        section = item.label;
        continue;
      }
      items.push({
        id: `${PROJECT_TAB_PREFIX}${item.id}`,
        label: item.label,
        sfSymbol: item.sfSymbol,
        section,
      });
    }
    return items;
  };

  const procurementSectionItems = () => [
    {
      id: `${PROJECT_TAB_PREFIX}overview`,
      label: 'Project',
      sfSymbol: 'chevron.left',
      section: 'main',
    },
    ...PROJECT_PROCUREMENT_SECTIONS.map((item) => ({
      id: `${PROJECT_TAB_PREFIX}${item.id}`,
      label: item.label,
      sfSymbol: item.sfSymbol,
      section: 'Procurement',
    })),
  ];

  const profileSectionItems = (personalSection: string) => {
    const items: { id: string; label: string; sfSymbol?: string; section: string }[] = [];
    let section = personalSection;
    for (const item of buildProfileSidebarTabItems({
      includeWorkspaceSettings: canManageWorkspaceSettings,
      includeAgents: role === 'super_admin',
      includePlugins: canViewWorkspacePlugins,
    })) {
      if (item.kind === 'heading') {
        section = item.label;
        continue;
      }
      items.push({
        id: `${PROFILE_TAB_PREFIX}${item.id}`,
        label: item.label,
        sfSymbol: item.sfSymbol,
        section,
      });
    }
    return items;
  };

  const settingsSectionItems = () => {
    let section = 'Settings';
    return buildSystemSettingsSidebarTabItems({includeOutreachTemplates,role}).flatMap(item => {
      if (item.kind === 'heading') { section = item.label; return []; }
      return [{id: `${SYSTEM_SETTINGS_TAB_PREFIX}${item.id}`, label: item.label, sfSymbol: item.sfSymbol, section}];
    });
  };

  if (hasNativeTabBar) {
    // Native iOS tab bar: the first 4 items pin as tabs, the rest fall under
    // "More". Pin Home, Projects, Messages, Alerts (notifications) — Calendar
    // and everything else live under More. The 'notifications' id already has
    // badge plumbing (updateBadge) and a web route, so the Alerts tab badges
    // the unread count and opens the notifications view on tap.
    const pipelineId = baseItems.find(item => isPipelineDestination(item.id))?.id;
    const pinnedOrder = ['dashboard', pipelineId ?? 'projects', 'messages'];
    const alertsItem = {
      id: 'notifications',
      label: 'Alerts',
      sfSymbol: 'bell.fill',
      section: 'main' as const,
    };
    const pinned = pinnedOrder
      .map((id) => baseItems.find((item) => item.id === id))
      .filter((item): item is NonNullable<typeof item> => Boolean(item));
    pinned.push(alertsItem);
    const pinnedIds = new Set([...pinnedOrder, 'notifications']);
    const rest = baseItems.filter((item) => !pinnedIds.has(item.id));

    // The tabs stay GLOBAL inside a project — they are top-level peer
    // categories, which 16 project sections in 4 groups are not. The sections
    // ride at the END of the list purely as DATA for the shell's own native
    // push-aside menu, which selects them by their `project-tab:` prefix; the
    // overflow sheet drops them by the same prefix so they never become tabs.
    if (currentView === 'project-detail' && projectTabProps) {
      const sections = isProjectProcurementTab(projectActiveTab)
        ? procurementSectionItems()
        : projectSectionItems();
      return [...pinned, ...rest, ...sections];
    }

    if (currentView === 'employers' && employerDetailOpen && !employerFormSectionIds?.length && hasNativeEmployerSectionMenu()) {
      const sections: { id: string; label: string; sfSymbol?: string; section: string }[] = [];
      for (const item of buildEmployerDetailSidebarTabItems()) {
        if (item.kind === 'heading') continue;
        sections.push({ id: `employers-tab:${item.id}`, label: item.label, sfSymbol: item.sfSymbol, section: 'main' });
      }
      return [...pinned, ...rest, ...sections];
    }

    if (currentView === 'client-detail' && hasNativeWorkspaceSectionMenu()) {
      return [...pinned, ...rest, ...WORKSPACE_NATIVE_SECTIONS.map((item) => ({
        ...item, id: `client-detail-tab:${item.id}`, section: 'main',
      }))];
    }

    if (hasNativeSectionMenu()) {
      const sections = currentView === 'sales'
        ? visibleSalesSections(role).map(({ id, label }) => ({
            id: `${SALES_TAB_PREFIX}${id}`, label, sfSymbol: SALES_SECTION_SYMBOLS[id] ?? 'circle', section: 'main',
          }))
        : currentView === 'purchases'
          ? visiblePurchasesSections(role).map(({ id, label }) => ({
              id: `${PURCHASES_TAB_PREFIX}${id}`, label, sfSymbol: PURCHASES_SECTION_SYMBOLS[id] ?? 'circle', section: 'main',
            }))
          : currentView === 'payroll'
            ? buildNativeFinanceHubItems(buildPayrollSidebarTabItems(), PAYROLL_TAB_PREFIX)
            : currentView === 'accounting'
              ? buildNativeFinanceHubItems(buildAccountingSidebarTabItems(), ACCOUNTING_TAB_PREFIX)
              : currentView === 'tax'
                ? buildNativeFinanceHubItems(buildTaxSidebarTabItems(), TAX_TAB_PREFIX)
                : [];
      if (sections.length) return [...pinned, ...rest, ...sections];
    }

    // Profile rides the same rail. Its seven panels (Details, Notifications,
    // Calls, Signature, Devices, Privacy) are sidebar-driven, and there is no
    // sidebar on a phone — so before this, only the Overview card was ever
    // reachable and the rest of the screen simply did not exist there.
    // The finance hubs deliberately publish NOTHING here. Their sections are
    // reached through the page's own chip row on a phone (see
    // shared/ui/section-chip-row), so rows here would only build a second menu
    // saying the same thing — and the shell selects section rows by prefix, so
    // any it does not recognise would fall through and become tabs.
    if (currentView === 'profile' || currentView === 'account') {
      // Same two groups the sidebar shells publish, so the phone's menu is the
      // desktop sidebar: the personal sections, then Settings. Gated on the
      // same role test — showing a Settings row to someone who cannot open the
      // view would route them into a blank gate.
      const canOpenSettings =
        role === 'super_admin' || role === 'admin' || role === 'internal' || role === 'staff';
      return [
        ...pinned,
        ...rest,
        // 'main' is the header-less bucket, so the personal sections lead
        // without a lonely heading above them.
        ...profileSectionItems('main'),
        // The section string IS the heading the menu renders.
        ...(canOpenSettings
          ? settingsSectionItems()
          : []),
      ];
    }

    return [...pinned, ...rest];
  }

  // Sidebar shells (macOS) reach Profile through the account footer menu;
  // a sidebar row would duplicate it. Tab-bar shells keep it (their More
  // menu is where Profile lives), hence the filter sits after that return.
  if (hasNativeSidebar) {
    const profileIndex = baseItems.findIndex((item) => item.id === 'profile');
    if (profileIndex !== -1) {
      baseItems.splice(profileIndex, 1);
    }
  }

  if (currentView === 'project-detail' && projectTabProps) {
    if (isProjectProcurementTab(projectActiveTab)) {
      return procurementSectionItems();
    }

    // RAMS editor open inside H&S: the document outline becomes the actual
    // macOS sidebar. The toolbar chevron already walks back to the record, so
    // this level needs section rows only, with no duplicate back row.
    if (hasNativeSidebar && hsEditorSectionIds?.length) {
      const visible = new Set(hsEditorSectionIds);
      return HS_EDITOR_SECTION_ITEMS
        .filter((item) => visible.has(item.id))
        .map(({ id, label, sfSymbol }) => ({
          id: `${HS_EDITOR_SECTION_PREFIX}${id}`,
          label,
          sfSymbol,
          section: 'RAMS',
        }));
    }

    if (hasNativeSidebar && projectActiveTab === 'hs' && hsDocumentSectionIds?.length) {
      const visible = new Set(hsDocumentSectionIds);
      return HS_DOCUMENT_SECTION_ITEMS
        .filter((item) => visible.has(item.id))
        .map(({ id, label, sfSymbol }) => ({
          id: `${HS_DOCUMENT_SECTION_PREFIX}${id}`,
          label,
          sfSymbol,
          section: 'RAMS',
        }));
    }

    // An interim application is open: drill one level deeper than the
    // project scope, to the application's own sections — the same move the
    // project scope makes from the main nav. Sidebar shells replace the web's
    // in-content SECTIONS rail outright; tab-bar shells get a back row to the
    // Applications tab plus the sections.
    if (interimApplicationOpen) {
      const sectionItems = INTERIM_SECTION_ITEMS
        .filter(({ id }) => id !== 'pay-less' || projectTabProps.canGivePayLessNotice === true)
        .map(({ id, label, sfSymbol }) => ({
        id: `${INTERIM_SECTION_PREFIX}${id}`,
        label,
        sfSymbol,
        section: 'application',
        }));
      return hasNativeSidebar
        ? [...sectionItems]
        : [
            {
              id: `${PROJECT_TAB_PREFIX}applications`,
              label: 'Back to Valuations',
              sfSymbol: 'chevron.left',
              section: 'application',
            },
            ...sectionItems,
          ];
    }

    const projectItems = projectSectionItems();

    // In a project, drill the sidebar in to JUST the project's own navigation.
    // The macOS shell has the toolbar back chevron (backTarget: 'projects'), so
    // it needs no sidebar back row; non-macOS shells get a "Back to Projects" row
    // (and keep the full app nav beneath). Previously the macOS sidebar appended
    // the project nav UNDER the entire app nav — noise inside a project.
    return hasNativeSidebar
      ? [...projectItems]
      : [
          {
            id: 'projects',
            label: 'Back to Projects',
            sfSymbol: 'chevron.left',
            section: 'project',
          },
          ...projectItems,
          ...baseItems,
        ];
  }

  // Profile and Settings are one area on the macOS sidebar. Opening either
  // shows the same combined list — the personal sections (Overview, Details,
  // Notifications, …) and, for anyone who can open Settings, the workspace
  // sections (General, Integrations, Audit Trail, …) — so you can jump straight
  // from a personal section to a settings one without the sidebar swapping out
  // from under you. Only the highlighted row differs between the two views, and
  // each row already routes to the right view by its id prefix.
  // Personal Preferences are open to everyone, so unlike Settings there is no
  // role gate; the sections are the same list the web sidebar shows.
  if (currentView === 'preferences') {
    const home = { id: 'dashboard', label: 'Home', sfSymbol: 'house.fill', section: 'preferences' };
    const sectionItems = PREFERENCE_SECTIONS.map(({ id, label, sfSymbol }) => ({
      id: `${PREFERENCES_TAB_PREFIX}${id}`, label, sfSymbol, section: 'preferences',
    }));
    if (hasNativeSidebar) return [home, ...sectionItems];
    return [
      { ...home, label: 'Back to Dashboard', sfSymbol: 'chevron.left' },
      ...sectionItems,
      ...baseItems,
    ];
  }

  if (currentView === 'profile' || currentView === 'system-settings') {
    if (hasNativeSidebar && currentView === 'profile' && userFormOpen) {
      return USER_FORM_SECTION_ITEMS.map(({ id, label, sfSymbol }) => ({
        id: `${USER_FORM_SECTION_PREFIX}${id}`, label, sfSymbol, section: 'user',
      }));
    }
    // Match the settings-view gate (auth.isAdmin() at
    // AuthenticatedWorkspace.tsx:773 → super_admin | internal | admin | staff):
    // showing a settings row to someone who cannot open the view would route
    // them into a blank gate. Everyone keeps the personal sections.
    const canOpenSettings =
      role === 'super_admin' || role === 'admin' || role === 'internal' || role === 'staff';

    const profileItems = profileSectionItems('profile');
    const settingsItems = canOpenSettings
      ? settingsSectionItems()
      : [];

    // Home is pinned above the sections as the way out: the profile/settings
    // sections replace the app nav on the macOS sidebar, and no toolbar back
    // chevron renders on these views. The web sidebar keeps the same Home
    // escape-hatch in focused mode.
    const home = { id: 'dashboard', label: 'Home', sfSymbol: 'house.fill', section: 'profile' };

    if (hasNativeSidebar) {
      return [home, ...profileItems, ...settingsItems];
    }
    // iOS keeps the whole app nav beneath, so it stays focused on the view you
    // opened rather than cross-listing both groups.
    const ownItems = currentView === 'profile' ? profileItems : settingsItems;
    return [
      { ...home, label: 'Back to Dashboard', sfSymbol: 'chevron.left', section: currentView === 'profile' ? 'profile' : 'settings' },
      ...ownItems,
      ...baseItems,
    ];
  }

  if (currentView === 'workspaces') {
    // A workspace is open on lg+: drill the macOS sidebar in further, to the
    // workspace's own section nav (Organisation / Accounts / Quantis /
    // Workspace) instead of the Directory/Requests/Add Workspace list. Only
    // the sidebar shell drills this deep — iOS tab-bar shells keep the
    // directory list and switch sections via WorkspaceDetail's own in-content
    // nav (hideInternalNav is a sidebar-shell-only concern).
    // The edit-user form is open inside the workspace's Users tab: drill one
    // level past the workspace sections to the form's own, exactly as the
    // interim branch does for an open application. Without this the native
    // sidebar would sit a level behind the web content, still listing
    // workspace sections while the form is showing.
    if (hasNativeSidebar && workspaceDetail && userFormOpen) {
      const sectionItems = USER_FORM_SECTION_ITEMS.map(({ id, label, sfSymbol }) => ({
        id: `${USER_FORM_SECTION_PREFIX}${id}`,
        label,
        sfSymbol,
        section: 'user',
      }));
      return sectionItems;
    }

    if (hasNativeSidebar && workspaceDetail) {
      const detailItems: { id: string; label: string; sfSymbol?: string; section: string }[] = [];
      let currentSection = 'clients';
      for (const item of buildWorkspaceDetailSidebarTabItems()) {
        if (item.kind === 'heading') {
          currentSection = item.label;
          continue;
        }
        detailItems.push({
          id: `${WORKSPACE_DETAIL_TAB_PREFIX}${item.id}`,
          label: item.label,
          sfSymbol: item.sfSymbol,
          section: currentSection,
        });
      }
      return detailItems;
    }

    const clientItems = buildWorkspacesSidebarTabItems({
      isAdmin,
      pendingRequests: workspacesStats?.pendingRequests,
    }).map(({ id, label, sfSymbol }) => ({
      id: `${WORKSPACES_TAB_PREFIX}${id}`,
      label,
      sfSymbol,
      section: 'clients',
    }));

    // Workspaces is a focused admin area, so the macOS sidebar drills in to
    // JUST its own items (same as project-detail); the native toolbar back
    // chevron is the way out (buildNativeHeaderState retargets it at Home for
    // this view). Appending the whole app nav underneath read as noise.
    // Non-macOS shells keep the back row + full nav.
    return hasNativeSidebar
      ? [...clientItems]
      : [
          {
            id: 'dashboard',
            label: 'Back to Dashboard',
            sfSymbol: 'chevron.left',
            section: 'clients',
          },
          ...clientItems,
          ...baseItems,
        ];
  }

  // The add/edit form is open: one level deeper than the profile below, so it
  // is checked first — the two are mutually exclusive on screen but they arrive
  // on independent channels, and a frame where both look open must resolve to
  // the deeper one. No headings in this list, hence the flat map.
  if (hasNativeSidebar && currentView === 'employers' && employerFormSectionIds?.length) {
    return buildEmployerFormSidebarTabItems(employerFormSectionIds).map(({ id, label, sfSymbol }) => ({
      id: `${EMPLOYER_FORM_TAB_PREFIX}${id}`,
      label,
      sfSymbol,
      section: 'employer',
    }));
  }

  // An employer profile is open: the sidebar drills in to its sections, same
  // move the workspace detail makes above. The toolbar chevron
  // (EMPLOYERS_CLOSE_DETAIL_SENTINEL) is the way out, so no back row here.
  if (hasNativeSidebar && currentView === 'employers' && employerDetailOpen) {
    const detailItems: { id: string; label: string; sfSymbol?: string; section: string }[] = [];
    let currentSection = 'employer';
    for (const item of buildEmployerDetailSidebarTabItems()) {
      if (item.kind === 'heading') {
        currentSection = item.label;
        continue;
      }
      detailItems.push({
        id: `${EMPLOYER_DETAIL_TAB_PREFIX}${item.id}`,
        label: item.label,
        sfSymbol: item.sfSymbol,
        section: currentSection,
      });
    }
    return detailItems;
  }

  // Finance hubs drill the native sidebar into the current workflow, matching
  // the browser sidebar. The old append left the whole Quantis menu above a
  // second Payroll/Accounting/Tax menu and made the column needlessly long.
  // Non-sidebar shells keep their compact in-content picker.
  if (hasNativeSidebar && currentView === 'payroll') {
    return buildNativeFinanceHubItems(buildPayrollSidebarTabItems(), PAYROLL_TAB_PREFIX);
  }

  if (hasNativeSidebar && currentView === 'accounting') {
    return buildNativeFinanceHubItems(buildAccountingSidebarTabItems(), ACCOUNTING_TAB_PREFIX);
  }

  // Sales DRILLS IN rather than appending, unlike the four hubs above: its
  // sections replace the app nav, the same move Workspaces and the employer
  // profile make here and the same one the web sidebar makes. Appending left
  // the macOS sidebar showing the whole app plus a Sales group, which is the
  // two-navs-at-once layout the drill-in convention exists to prevent.
  // The toolbar chevron is the way out — see macSidebarDrillIn, which `sales`
  // is a member of, without which this would strand the user in the hub.
  if (hasNativeSidebar && currentView === 'sales') {
    return visibleSalesSections(role).map(({ id, label }) => ({
      id: `${SALES_TAB_PREFIX}${id}`,
      label,
      sfSymbol: SALES_SECTION_SYMBOLS[id] ?? 'circle',
      // 'main' is the native side's "render outside any group" bucket. These
      // rows are the entire sidebar now, and the toolbar title already reads
      // Sales, so a 'sales' group would print that word twice on one screen.
      section: 'main',
    }));
  }

  // Purchases made the same move as Sales, for the same reason: its sections
  // now replace the app nav rather than appending to it, so the toolbar
  // chevron (see macSidebarDrillIn) is the only way out.
  if (hasNativeSidebar && currentView === 'purchases') {
    return visiblePurchasesSections(role).map(({ id, label }) => ({
      id: `${PURCHASES_TAB_PREFIX}${id}`,
      label,
      sfSymbol: PURCHASES_SECTION_SYMBOLS[id] ?? 'circle',
      // 'main' is the native side's "render outside any group" bucket — see
      // the Sales branch above for why a named group would double the title.
      section: 'main',
    }));
  }

  if (hasNativeSidebar && currentView === 'tax') {
    return buildNativeFinanceHubItems(buildTaxSidebarTabItems(), TAX_TAB_PREFIX);
  }

  // Email folder sub-navigation — macOS sidebar only, so switching mailboxes
  // is a native click instead of round-tripping through the in-content
  // MailboxSelect. Email is a focused workspace: the mailboxes replace the
  // product navigation while the toolbar chevron and Home row provide the
  // escape hatch. Shared inboxes ('group:' ids, injected by AppSidebar from
  // the email_groups registry) are out of scope for this pass.
  if (hasNativeSidebar && currentView === 'email' && (role === 'super_admin' || role === 'internal')) {
    const emailItems = buildEmailSidebarTabItems().map(({ id, label, sfSymbol }) => ({
      id: `${EMAIL_TAB_PREFIX}${id}`,
      label,
      sfSymbol,
      section: 'main',
    }));
    const sharedItems = emailSharedMailboxes.map(({ id, label }) => ({
      id: `${EMAIL_SHARED_TAB_PREFIX}${id}`,
      label,
      sfSymbol: 'tray.2.fill',
      section: 'Shared Mailboxes',
    }));
    return [
      { id: 'dashboard', label: 'Home', sfSymbol: 'house.fill', section: 'main' },
      ...emailItems,
      ...(sharedItems.length > 0
        ? sharedItems
        : []),
    ];
  }

  return baseItems;
}

export function getNativeRouteItemIdentifier({
  currentView,
  pipelineMainItemId,
  accountingMainItemId,
  interimApplicationOpen = false,
  interimActiveSection = null,
  hsEditorActiveSection = null,
  hsDocumentActiveSection = null,
  projectActiveTab,
  systemSettingsActiveTab,
  preferencesSection,
  profileActiveTab,
  workspacesActiveTab,
  workspaceDetail,
  payrollActiveTab,
  accountingActiveTab,
  salesActiveTab,
  purchasesActiveTab,
  taxActiveTab,
  emailActiveTab,
  hasNativeSidebar = false,
  hasNativeTabBar = false,
}: {
  currentView: AppView;
  pipelineMainItemId?: string | null;
  accountingMainItemId?: string | null;
  /** Open interim application: the active sidebar row is its section, not
   *  the project tab. */
  interimApplicationOpen?: boolean;
  interimActiveSection?: string | null;
  hsEditorActiveSection?: string | null;
  hsDocumentActiveSection?: string | null;
  projectActiveTab: string;
  systemSettingsActiveTab: string;
  preferencesSection?: string | null;
  profileActiveTab?: string;
  workspacesActiveTab: 'clients' | 'requests' | 'add-client';
  /** Open workspace on lg+; when set, the native selection should highlight
   *  its active section row instead of the directory tab. */
  workspaceDetail?: { clientId: string; clientName: string; activeTab: string } | null;
  payrollActiveTab?: string | null;
  accountingActiveTab?: string | null;
  salesActiveTab?: string | null;
  purchasesActiveTab?: string | null;
  taxActiveTab?: string | null;
  emailActiveTab?: string | null;
  hasNativeSidebar?: boolean;
  hasNativeTabBar?: boolean;
}) {
  if (hasNativeTabBar && currentView === 'project-detail') {
    return pipelineMainItemId ?? 'projects';
  }

  // The tab bar only holds APP-level tabs plus More, so it must be given an
  // app-level id. Profile otherwise resolved to `profile-tab:overview`, which
  // matches no tab and no overflow item, and the shell falls back to the first
  // tab — so opening Profile from More left Home highlighted. Returning the
  // plain view id lets it match the 'profile' overflow row and select More.
  if (hasNativeTabBar && (currentView === 'profile' || currentView === 'account')) {
    return currentView;
  }

  if (isPipelineDestination(currentView) && pipelineMainItemId) return pipelineMainItemId;

  if (currentView === 'project-detail') {
    if (hasNativeSidebar && hsEditorActiveSection) {
      return `${HS_EDITOR_SECTION_PREFIX}${hsEditorActiveSection}`;
    }
    if (hasNativeSidebar && projectActiveTab === 'hs' && hsDocumentActiveSection) {
      return `${HS_DOCUMENT_SECTION_PREFIX}${hsDocumentActiveSection}`;
    }
    if (interimApplicationOpen) {
      return `${INTERIM_SECTION_PREFIX}${interimActiveSection || 'summary'}`;
    }
    // Each report is a page of the one Reports row the native rail lists.
    const nativeProjectTab = isProjectReportsTab(projectActiveTab) ? 'reports' : projectActiveTab;
    return `${PROJECT_TAB_PREFIX}${nativeProjectTab || 'overview'}`;
  }

  if (currentView === 'system-settings') {
    return `${SYSTEM_SETTINGS_TAB_PREFIX}${systemSettingsActiveTab || 'integrations'}`;
  }

  if (currentView === 'preferences') {
    return hasNativeSidebar && preferencesSection
      ? `${PREFERENCES_TAB_PREFIX}${preferencesSection}`
      : currentView;
  }

  if (currentView === 'profile' || currentView === 'account') {
    const selectedProfileTab = profileActiveTab?.startsWith('workspace/bots/')
      ? 'workspace/bots'
      : profileActiveTab?.startsWith('workspace/users/')
      ? 'workspace/users'
      : profileActiveTab?.startsWith('workspace/documents/')
      ? 'workspace/documents'
      : profileActiveTab?.startsWith('preferences/') ? `preferences/${settingsTabFor(profileActiveTab.slice('preferences/'.length)) ?? 'general'}` : profileActiveTab === 'calls' ? 'availability' : profileActiveTab || 'overview';
    return `${PROFILE_TAB_PREFIX}${selectedProfileTab}`;
  }

  if (currentView === 'workspaces') {
    return workspaceDetail
      ? `${WORKSPACE_DETAIL_TAB_PREFIX}${workspaceDetail.activeTab}`
      : `${WORKSPACES_TAB_PREFIX}${workspacesActiveTab || 'clients'}`;
  }

  if (hasNativeSidebar && currentView === 'payroll' && payrollActiveTab) {
    return `${PAYROLL_TAB_PREFIX}${payrollActiveTab}`;
  }

  if (hasNativeSidebar && currentView === 'accounting' && accountingActiveTab) {
    return `${ACCOUNTING_TAB_PREFIX}${accountingActiveTab}`;
  }

  if (hasNativeSidebar && currentView === 'sales' && salesActiveTab) {
    return `${SALES_TAB_PREFIX}${salesActiveTab}`;
  }

  if (hasNativeSidebar && currentView === 'purchases' && purchasesActiveTab) {
    return `${PURCHASES_TAB_PREFIX}${purchasesActiveTab}`;
  }

  if (hasNativeSidebar && currentView === 'tax' && taxActiveTab) {
    return `${TAX_TAB_PREFIX}${taxActiveTab}`;
  }

  // Only report prefixed ids for labels that exist as rendered native rows
  // (the buildEmailSidebarTabItems set that buildNativeNavItems emits).
  // Anything else — shared inboxes ('group:' ids), the in-content picker's
  // UNREAD mailbox — falls through to the plain 'email' id so native doesn't
  // try to highlight a row that doesn't exist.
  if (hasNativeSidebar && currentView === 'email' && emailActiveTab
      && buildEmailSidebarTabItems().some((item) => item.id === emailActiveTab)) {
    return `${EMAIL_TAB_PREFIX}${emailActiveTab}`;
  }

  if (hasNativeSidebar && currentView === 'email' && emailActiveTab?.startsWith('group:')) {
    return `${EMAIL_SHARED_TAB_PREFIX}${emailActiveTab.slice('group:'.length)}`;
  }

  if (isAccountingDestination(currentView) && accountingMainItemId) return accountingMainItemId;
  return currentView;
}
